/* eslint-disable @typescript-eslint/no-unused-vars */
// Utility to map API response to app's expected format
export function mapApiWorkDataToAppFormat(apiData: any[]): any[] {
  return apiData.map((item) => {
    const mapped: any = {
      id: item.ID,
      SiteEvaluationaForm: item.Section_Value,
      parentId: item.Parent_Id === "NULL" ? undefined : item.Parent_Id,
      recommendActionPlan: false,
    };
    if (item.Section_Type === "HEADER") {
      mapped.isHeader = true;
    } else if (item.Section_Type === "SubHeader") {
      mapped.toggleOnly = true;
      mapped.isParent = true;
    } else if (item.Section_Type === "DATA") {
      mapped.yesChecked = false;
      mapped.noChecked = false;
      mapped.naChecked = false;
    }
    return mapped;
  });
}
import React, { useState, useRef, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
  TextInput,
  ScrollView,
  TouchableOpacity,
} from "react-native";
import Svg, { Path } from "react-native-svg";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "../../store";
import LhButton from "../../components/LhButton";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import LHOverlay from "../../components/LHOverlay";
import SiteEvalActionPlanOverlay from "./components/SiteEvalActionPlanOverlay";
import SiteEvalDataTable from "./SiteEvalDataTable";
import SiteEvalSurveyContainer from "./components/SiteEvalSurveyContainer";
import LHConfirmationDialog from "../../components/LHConfirmationDialog";
import { getOrCreateSiteEvalSessionId, clearSiteEvalSessionId } from './utils/sessionIdGenerator';
import {
  selectPreWorkData,
  selectPreWorkColumns,
  selectWorkData,
  selectWorkColumns,
  selectCloseOutData,
  selectCloseOutColumns,
  selectWalkAnswers,
  setWalkAnswer,
  setWalkOverlayData,
  selectCloseOutAnswers,
  setCloseOutAnswer,
  removeCloseOutAnswer,
  resetSiteEvalRedirect,
  removeWalkAnswer,
  selectPreWorkAnswers,
  setPreWorkAnswers,
  setPreWorkTarget,
  selectPreWorkTargets,
  selectActionPlanSessionId,
  setActionPlanSessionId,
  fetchAllQuestionsData,
  selectSiteEvalActionPlans,
  addSiteEvalActionPlan,
  updateSiteEvalActionPlan,
  clearSiteEvalActionPlans,
  setWorkData,
  setPreWorkData,
  setCloseOutData,
  // removeWalkAnswer
} from "../../store/siteEvalRedirectSlice";
import {
  handleSiteEvaluationErrorWithFriendlyMessage,
  safeSiteEvaluationApiCall,
  SITE_EVALUATION_API_NAMES,
} from '../../store/utils/siteEvaluationErrorHandler';
import SiteEvalRedirectAction from "./components/SiteEvalRedirectAction";
import LHLoader from "../../components/LHLoader";
import CrossPlatformDropdown from "../../components/CrossPlatformDropdown";
import { formatUserName } from "../../utils/emailTouserName";
import { formatDate } from "../../utils/dateFormatter";
import DLOBreadcrumb, { DLOBreadcrumbItem } from "../../modules/dlo/components/DLOBreadcrumb";
import DLOTooltip from "../../modules/dlo/components/DLOTooltip";
import FontFamily from "../../constants/fonts";
import SiteEvaluationErrorModal from './components/SiteEvaluationErrorModal';
import { useTheme } from "../../theme";

// Add icon component for Action Plan
const AddIcon: React.FC = () => (
  <Svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <Path
      d="M6 0.5V11.5M0.5 6H11.5"
      stroke="#005CBC"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// Edit icon for completed action plans
const EditIcon: React.FC = () => (
  <Svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <Path
      d="M10.545 6.765L11.235 7.455L4.44 14.25H3.75V13.56L10.545 6.765ZM13.245 2.25C13.0575 2.25 12.8625 2.325 12.72 2.4675L11.3475 3.84L14.16 6.6525L15.5325 5.28C15.825 4.9875 15.825 4.515 15.5325 4.2225L13.7775 2.4675C13.6275 2.3175 13.44 2.25 13.245 2.25ZM10.545 4.6425L2.25 12.9375V15.75H5.0625L13.3575 7.455L10.545 4.6425Z"
      fill="#005CBC"
    />
  </Svg>
);

// Info icon for completed action plans
const InfoIcon: React.FC = () => (
  <Svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <Path
      d="M8.25 5.25H9.75V6.75H8.25V5.25ZM8.25 8.25H9.75V12.75H8.25V8.25ZM9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM9 15C5.6925 15 3 12.3075 3 9C3 5.6925 5.6925 3 9 3C12.3075 3 15 5.6925 15 9C15 12.3075 12.3075 15 9 15Z"
      fill="#005CBC"
    />
  </Svg>
);

interface SiteEvalRedirectScreenProps {
  onBackPress: () => void;
  onHomePress?: () => void;
  showStatusMessage?: (type: "success" | "error", message: string) => void;
}

// Exported for testing
export const isMobileView = () => {
  const windowWidth = Dimensions.get("window").width;
  return (
    Platform.OS === "ios" || Platform.OS === "android" || windowWidth < 768
  );
};

// Function to detect iPad specifically
// Exported for testing
export const isIPadView = () => {
  const windowWidth = Dimensions.get("window").width;
  return Platform.OS === "ios" && windowWidth >= 768;
};

// Stepper navigation component (continuous connected line)
const StepperNavigation: React.FC<{
  active: string;
  onChange: (id: string) => void;
  style?: any;
  disabled?: boolean;
}> = ({ active, onChange, style, disabled = false }) => {
  const { colors: snColors, resolvedTheme: snResolvedTheme } = useTheme();
  const snIsDark = snResolvedTheme === "dark";
  const steps = [
    { id: "pre-work", title: "Pre Work" },
    { id: "vpe-mhe", title: "VP Equipment/MHE/Supplies/Pre-Shift" },
    { id: "yard-gate", title: "Yard/Gate" },
    { id: "layer-redzone", title: "Layer/Red Zone/Docks" },
    { id: "pick-area", title: "Pick Area" },
    { id: "deep-reserve", title: "Deep Reserve/Chilled/Repack" },
    { id: "regulatory", title: "Regulatory/Documentation" },
    { id: "lighthouse", title: "Lighthouse" },
  ];

  const activeIndex = steps.findIndex((s) => s.id === active);

  // Responsive layout calculations
  const windowWidth = Dimensions.get('window').width;
  const mobile = isMobileView();
  const iPad = isIPadView();

  // spacing and sizes adjust for mobile / iPad
  // For iPad, set stepper bar width to match the below container (tableCard)
  const tableCardHorizontalPadding = 24; // matches tableCard padding in styles
  const tableCardMaxWidth = 900; // matches maxWidth if you have one for tableCard
  const baseSpacing = mobile ? 6 : 24; // Remove iPad spacing since we'll use space-between
  // Increase vertical spacing on iPad so the track and labels have breathing room
  const paddingVertical = mobile ? 6 : iPad ? 28 : 8;
  // Reduce iPad base circle size to improve fit
  const circleSize = mobile ? 14 : iPad ? 16 : 16;
  // Make the progress bar thicker on iPad for better visibility
  const progressBarHeight = mobile ? 2 : iPad ? 22 : 2;

  // compute stepWidth to ensure entire stepper fits on small screens
  let stepWidth: number;
  let contentWidth: number | string;
  if (mobile) {
    const horizontalPadding = 16;
    const available = Math.max(windowWidth - horizontalPadding, 180);
    const calc = Math.floor((available - (steps.length - 1) * baseSpacing) / steps.length);
    stepWidth = Math.max(45, Math.min(calc, 56));
    contentWidth = steps.length * stepWidth + Math.max(0, steps.length - 1) * baseSpacing;
  } else if (iPad) {
    // On iPad, match the table card width (maxWidth and padding) so stepper lines up with the card
    const available = Math.min(windowWidth - tableCardHorizontalPadding * 2, tableCardMaxWidth);
    stepWidth = 0; // Not used for space-between layout
    contentWidth = available; // use capped available width to align left with table card
  } else {
    stepWidth = 120;
    contentWidth = steps.length * stepWidth + Math.max(0, steps.length - 1) * baseSpacing;
  }

  // Remove stepperContainerHeight, not needed for horizontal alignment

  const stepSpacing = baseSpacing;
  // measure rendered width on iPad so we can lay out circles exactly over the table card width
  const [containerWidth, setContainerWidth] = React.useState<number | null>(null);
  // For iPad with space-between, calculate progress width differently
  let completedWidth: number;
  if (iPad) {
    // With space-between, calculate progress as percentage of total width
    const progressPercentage = Math.max(0, activeIndex) / Math.max(1, steps.length - 1);
    completedWidth = typeof contentWidth === 'number' ? contentWidth * progressPercentage : 0;
  } else {
    completedWidth = Math.max(0, activeIndex) * (stepWidth + stepSpacing);
  }

  return (
    <View style={[styles.stepperContainer, { alignItems: 'stretch', marginLeft: iPad ? 0 : mobile ? -8 : -24, marginRight: iPad ? 0 : 0, paddingHorizontal: iPad ? tableCardHorizontalPadding : mobile ? 8 : 0, marginBottom: iPad ? 32 : 12 }, style]}>
      {/* For iPad: render full stepper (track + circles + absolute labels) matching Figma — centers align with track ends and gaps equal */}
      {iPad ? (
        (() => {
          // Allow increasing horizontal gaps between circles by expanding the effective stepper width
          const baseAvailable = Math.min(windowWidth - tableCardHorizontalPadding * 2, tableCardMaxWidth);
          const segments = Math.max(1, steps.length - 1);
          // Aggressively compress gaps so all tabs fit on iPad without scrolling
          const desiredExtraPerSegment = -28; // more negative => smaller gaps
          const extraTotal = desiredExtraPerSegment * Math.max(0, segments - 1);
          // Don't exceed the full available window width minus horizontal padding
          const maxAllowedWidth = Math.max(0, windowWidth - tableCardHorizontalPadding * 2);
          let widthToUse = Math.min(maxAllowedWidth, baseAvailable + extraTotal);
          // prevent collapsing too small
          widthToUse = Math.max(360, widthToUse);
          if (!widthToUse || steps.length < 2) return null;

          // Keep effective circle size small on iPad; do not upscale
          let effectiveCircleSize = circleSize;
          if (widthToUse < 520) effectiveCircleSize = Math.max(12, circleSize - 4);

          // totalTrackWidth is distance between first and last circle centers (subtract a tiny padding)
          const totalTrackWidth = Math.max(0, widthToUse - effectiveCircleSize - 6);
          const segmentWidth = totalTrackWidth / segments;
          const bgLeft = effectiveCircleSize / 2 + 2;
          const bgWidth = totalTrackWidth;
          const completedW = Math.round(segmentWidth * Math.min(activeIndex, segments));
          // Tighten label width so labels don't force overflow
          const labelWidth = Math.min(140, Math.max(56, Math.floor(segmentWidth * 0.8)));

          // We compress layout to fit; disable horizontal scrolling preference so it always fits
          const needsScroll = false;

          // Add extra bottom padding for iPad stepper so the track has breathing room
          const extraStepperPaddingBottom = 20; // px additional bottom padding for iPad

          const innerStepper = (
            <View style={{ width: widthToUse, position: 'relative', paddingVertical, paddingBottom: paddingVertical + extraStepperPaddingBottom, alignSelf: 'flex-start' }}>
              {/* background track from first circle center to last circle center */}
              <View
                style={{
                  position: 'absolute',
                  left: bgLeft,
                  width: bgWidth,
                  height: progressBarHeight,
                  backgroundColor: snIsDark ? snColors.border.divider : '#E6E9EB',
                  top: paddingVertical + effectiveCircleSize / 2 - progressBarHeight / 2,
                  zIndex: 0,
                  borderRadius: progressBarHeight / 2,
                }}
              />

              {/* completed overlay - only fills completed segments between circle centers */}
              {completedW > 0 && (
                <View
                  style={{
                    position: 'absolute',
                    left: bgLeft,
                    width: completedW,
                    height: progressBarHeight,
                    backgroundColor: '#005CBC',
                    top: paddingVertical + effectiveCircleSize / 2 - progressBarHeight / 2,
                    zIndex: 1,
                    borderRadius: progressBarHeight / 2,
                  }}
                />
              )}

              {/* circles positioned by centerX = bgLeft + idx * segmentWidth */}
              {steps.map((step, idx) => {
                const isActive = idx === activeIndex;
                const isCompleted = idx < activeIndex;
                const centerX = bgLeft + idx * segmentWidth;
                return (
                  <TouchableOpacity
                    key={step.id}
                    onPress={() => { if (!disabled) onChange(step.id); }}
                    activeOpacity={disabled ? 1 : 0.8}
                    style={{
                      position: 'absolute',
                      left: centerX - effectiveCircleSize / 2,
                      width: effectiveCircleSize,
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2,
                      top: paddingVertical,
                      opacity: disabled ? 0.4 : 1,
                    }}
                  >
                    <View
                      style={[
                        styles.stepCircle,
                        { width: effectiveCircleSize, height: effectiveCircleSize, borderRadius: effectiveCircleSize / 2 },
                        isCompleted
                          ? styles.stepCircleCompleted
                          : isActive
                            ? styles.stepCircleActive
                            : styles.stepCircleInactive,
                        // Dark mode: inactive circle must be visible against dark background
                        !isCompleted && !isActive && snIsDark && {
                          backgroundColor: '#3A3F4A',
                          borderWidth: 1,
                          borderColor: '#5A6070',
                        },
                        // Light theme: inactive circle should be grey matching the track line color
                        !isCompleted && !isActive && !snIsDark && {
                          backgroundColor: '#E6E9EB',
                          borderWidth: 0,
                        },
                      ]}
                    >
                      {isCompleted && (
                        <MaterialIcons name="check" size={Math.max(8, Math.floor(effectiveCircleSize / 2))} color="#fff" />
                      )}
                    </View>
                  </TouchableOpacity>
                );
              })}

              {/* labels absolutely positioned under each circle using same centerX */}
              {steps.map((step, idx) => {
                const isActive = idx === activeIndex;
                const centerX = bgLeft + idx * segmentWidth;
                return (
                  <View
                    key={`label-${step.id}`}
                    style={{
                      position: 'absolute',
                      left: centerX - labelWidth / 2,
                      width: labelWidth,
                      alignItems: 'center',
                      top: paddingVertical + effectiveCircleSize + 8,
                      zIndex: 2,
                    }}
                  >
                    <Text style={[styles.stepLabel, isActive ? styles.stepLabelActive : styles.stepLabelInactive, snIsDark && !isActive && { color: snColors.text.secondary }]} numberOfLines={2}>
                      {step.title}
                    </Text>
                  </View>
                );
              })}
            </View>
          );

          if (needsScroll) {
            return (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ alignItems: 'flex-start' }}>
                {innerStepper}
              </ScrollView>
            );
          }

          return innerStepper;
        })()
      ) : (
        // Mobile: Scrollable stepper
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ alignItems: 'center', paddingHorizontal: mobile ? 8 : 0 }}
        >
          <View style={{ width: contentWidth, position: 'relative', paddingVertical }}>
            {/* continuous background line */}
            <View
              style={{
                position: 'absolute',
                left: stepWidth / 2,
                width: typeof contentWidth === 'number' ? Math.max(0, contentWidth - stepWidth) : '100%',
                height: progressBarHeight,
                backgroundColor: snIsDark ? snColors.border.divider : '#E6E9EB',
                top: paddingVertical + circleSize / 2,
                zIndex: 0,
                borderRadius: 1,
              }}
            />

            {/* completed overlay */}
            {completedWidth > 0 && (
              <View
                style={{
                  position: 'absolute',
                  left: stepWidth / 2,
                  width: completedWidth,
                  height: progressBarHeight,
                  backgroundColor: '#005CBC',
                  top: paddingVertical + circleSize / 2,
                  zIndex: 1,
                  borderRadius: 1,
                }}
              />
            )}

            {/* circles */}
            <View style={{ flexDirection: 'row', alignItems: 'center', zIndex: 2 }}>
              {steps.map((step, idx) => {
                const isActive = idx === activeIndex;
                const isCompleted = idx < activeIndex;
                return (
                  <View key={step.id} style={{ width: stepWidth, alignItems: 'center', marginRight: idx === steps.length - 1 ? 0 : stepSpacing }}>
                    <TouchableOpacity
                      style={{ alignItems: 'center', justifyContent: 'center' }}
                      onPress={() => { if (!disabled) onChange(step.id); }}
                      activeOpacity={disabled ? 1 : 0.7}
                      disabled={disabled}
                    >
                      <View
                        style={[
                          styles.stepCircle,
                          { width: circleSize, height: circleSize, borderRadius: circleSize / 2 },
                          isCompleted
                            ? styles.stepCircleCompleted
                            : isActive
                              ? styles.stepCircleActive
                              : styles.stepCircleInactive,
                          // Dark mode: inactive circle must be visible against dark background
                          !isCompleted && !isActive && snIsDark && {
                            backgroundColor: '#3A3F4A',
                            borderWidth: 1,
                            borderColor: '#5A6070',
                          },
                          // Light theme: inactive circle should be grey matching the track line color
                          !isCompleted && !isActive && !snIsDark && {
                            backgroundColor: '#E6E9EB',
                            borderWidth: 0,
                          },
                        ]}
                      >
                        {isCompleted && <MaterialIcons name="check" size={Math.max(8, Math.floor(circleSize / 2))} color="#fff" />}
                      </View>
                    </TouchableOpacity>
                  </View>
                );
              })}
            </View>
          </View>
        </ScrollView>
      )}

      {/* Labels row (visible on iPad and desktop, hidden on mobile) */}
      {!mobile && (
        iPad ? null : (
          // Desktop: Scrollable labels
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ alignItems: 'center', marginTop: 4 }}
          >
            <View style={{ width: contentWidth, flexDirection: 'row', alignItems: 'center' }}>
              {steps.map((step, idx) => {
                const isActive = idx === activeIndex;
                return (
                  <View key={step.id} style={{ width: stepWidth, alignItems: 'flex-start', marginRight: idx === steps.length - 1 ? 0 : stepSpacing, paddingLeft: (stepWidth - 100) / 2 }}>
                    <Text style={[styles.stepLabel, isActive ? styles.stepLabelActive : styles.stepLabelInactive, snIsDark && !isActive && { color: snColors.text.secondary }]} numberOfLines={2}>
                      {step.title}
                    </Text>
                  </View>
                );
              })}
            </View>
          </ScrollView>
        )
      )}
    </View>
  );
};

// Helper function to check if action plan exists (using Redux action plans)
const checkIfActionPlanExistsInRedux = (actionPlans: any[], itemId: string, evalId?: number | null) => {
  try {
    const uniqueKey = evalId ? `${evalId}_${itemId}` : itemId;
    return actionPlans.some((plan: any) => plan.itemId === uniqueKey || plan.relatedId === uniqueKey);
  } catch (error) {
    console.warn('Error checking action plan existence:', error);
    return false;
  }
};

// Helper function to get stored action plan data for editing (using Redux action plans)
const getStoredActionPlanFromRedux = (actionPlans: any[], itemId: string, evalId?: number | null) => {
  try {
    const uniqueKey = evalId ? `${evalId}_${itemId}` : itemId;
    const plan = actionPlans.find((plan: any) => plan.itemId === uniqueKey || plan.relatedId === uniqueKey);
    return plan || null;
  } catch (error) {
    console.warn('Error retrieving action plan data:', error);
    return null;
  }
};

// Helper: get section subtree rows for given header name(s) (case-insensitive contains match)
// Exported for testing
export const getSectionData = (rawData: any[], headerNames: string[]) => {
  if (!rawData || rawData.length === 0) return [];
  const lowerNames = headerNames.map((n) => n.toLowerCase());
  // find header ids that match any header name (contains, case-insensitive)
  const headerIds = rawData
    .filter((r) => r.isHeader && r.SiteEvaluationaForm)
    .filter((h) => lowerNames.some((n) => (h.SiteEvaluationaForm || '').toLowerCase().includes(n)))
    .map((h) => h.id);

  if (headerIds.length === 0) return [];

  // BFS to collect all descendant ids starting from header ids
  const includedIds = new Set<string>(headerIds.map((id) => String(id)));
  let queue = Array.from(includedIds);
  while (queue.length) {
    const pid = queue.shift();
    rawData.forEach((item) => {
      if (String(item.parentId) === String(pid) && !includedIds.has(String(item.id))) {
        includedIds.add(String(item.id));
        queue.push(String(item.id));
      }
    });
  }

  // Preserve original order
  const result = rawData.filter((item) => includedIds.has(String(item.id)));
  return result;
};

// Reusable mapper: merge answers into provided rows
// Exported for testing
export const mapAnswersToData = (rows: any[], answers: any[]) => {
  return rows.map((row: any) => {
    if (row.isHeader || row.toggleOnly) return row;
    const answerObj = answers.find((ans: any) => String(ans.id) === String(row.id));
    if (answerObj && answerObj.answer !== undefined && answerObj.answer !== null) {
      let yesChecked = false,
        noChecked = false,
        naChecked = false;
      if (typeof answerObj.answer === 'number') {
        yesChecked = answerObj.answer === 1;
        noChecked = answerObj.answer === 2;
        naChecked = answerObj.answer === 3;
      } else if (typeof answerObj.answer === 'string') {
        yesChecked = answerObj.answer === 'yes';
        noChecked = answerObj.answer === 'no';
        naChecked = answerObj.answer === 'na';
      }
      return { ...row, yesChecked, noChecked, naChecked };
    }
    return row;
  });
};

// Generic SectionTab component to render questions for specific header names
const SectionTab: React.FC<{
  headerNames: string[];
  handleSubmit: (action: 'save' | 'complete') => void;
  isSubmitting: boolean;
  setActiveTab?: (tabId: string) => void;
  nextTab?: string;
  currentTab?: string;
  isViewOnlyMode: boolean;
  selectedEvalId?: number | null;
}> = ({ headerNames, handleSubmit, isSubmitting, setActiveTab, nextTab, currentTab, isViewOnlyMode, selectedEvalId }) => {
  const { colors: stColors, resolvedTheme: stResolvedTheme } = useTheme();
  const stIsDark = stResolvedTheme === "dark";
  const rawWorkData = useSelector(selectWorkData) as any[];
  const walkAnswers = useSelector(selectWalkAnswers) as any[];
  const FieldUsers = useSelector((state: any) => state.appData.FieldUsers);
  const COEUsers = useSelector((state: any) => state.appData.COEUsers);
  const sectionRows = getSectionData(rawWorkData, headerNames);
  // For completed evaluations (view-only), only show questions that have a response
  const answeredIds = new Set(walkAnswers.map((ans: any) => String(ans.id)));
  const filteredSectionRows = isViewOnlyMode
    ? sectionRows.filter((row: any) => row.isHeader || row.toggleOnly || answeredIds.has(String(row.id)))
    : sectionRows;
  const sectionData = mapAnswersToData(filteredSectionRows, walkAnswers);

  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayTitle, setOverlayTitle] = useState('');
  const [currentOverlayId, setCurrentOverlayId] = useState<string | null>(null);
  const [currentQuestionTitle, setCurrentQuestionTitle] = useState<string>('');
  const [actionPlanText, setActionPlanText] = useState('');
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  const dispatch = useDispatch();

  const getWalkAnswerById = (id: string) => (walkAnswers || []).find((ans: any) => String(ans.id) === String(id));

  const handleRadioChange = (id: string, answer: 'yes' | 'no' | 'na') => {
    if (isViewOnlyMode) return;
    dispatch(setWalkAnswer({ id, answer }));
  };

  const handleToggleChange = (id: string, value: boolean, title: string, questionTitle?: string) => {
    if (isViewOnlyMode) return;
    if (value) {
      setOverlayTitle(`${title} Enabled`);
      const uniqueKey = selectedEvalId ? `${selectedEvalId}_${id}` : id;
      setCurrentOverlayId(uniqueKey);
      setCurrentQuestionTitle(questionTitle || title);
      setActionPlanText('');
      setSelectedSupervisor('');
      const answerObj = getWalkAnswerById(id);
      if (answerObj) {
        setActionPlanText(answerObj.actionPlan || '');
        setSelectedSupervisor(answerObj.supervisor || '');
      }
      setOverlayVisible(true);
    } else {
      dispatch(removeWalkAnswer(id));
    }
  };

  const handleOverlaySave = (actionPlan: string, supervisor: string) => {
    if (isViewOnlyMode) return;
    if (currentOverlayId) {
      // Extract the question ID from the composite key (evalId_questionId)
      let questionId = currentOverlayId;
      if (currentOverlayId.includes('_')) {
        const parts = currentOverlayId.split('_');
        questionId = parts[parts.length - 1]; // Get the last part (questionId)
      }
      dispatch(setWalkOverlayData({ id: questionId, actionPlan, supervisor }));
      // dispatch(setWalkAnswer({ id: questionId, answer: 'yes' }));
    }
    setOverlayVisible(false);
    setCurrentOverlayId(null);
    setActionPlanText('');
    setSelectedSupervisor('');
  };

  const handleOverlayDismiss = () => {
    setOverlayVisible(false);
    setCurrentOverlayId(null);
    setCurrentQuestionTitle('');
    setCurrentQuestionTitle('');
    setActionPlanText('');
    setSelectedSupervisor('');
  };

  return (
    <View style={[styles.tabContainer, stIsDark && { backgroundColor: stColors.background.surface }]}>
      <View style={[styles.surveyContainer, stIsDark && { backgroundColor: stColors.background.surface }]}>
        <SiteEvalSurveyContainer
          data={sectionData}
          walkAnswers={walkAnswers as any[]}
          onRadioChange={handleRadioChange}
          onToggleChange={handleToggleChange}
          isViewOnlyMode={isViewOnlyMode}
          selectedEvalId={selectedEvalId}
          testID="section-survey"
        />
      </View>

      <SiteEvalActionPlanOverlay
        visible={overlayVisible}
        onDismiss={handleOverlayDismiss}
        title={overlayTitle}
        questionId={currentOverlayId || ''}
        initialActionPlan={actionPlanText}
        initialSupervisor={selectedSupervisor}
        onSave={handleOverlaySave}
        isViewOnlyMode={isViewOnlyMode}
      />

      {/* previously per-tab action buttons were rendered inside each tab; removed to render a unified action row outside the card */}
    </View>
  );
};

// Modify WalkTab to show only 'Walk' header questions
const WalkTab = ({ handleSubmit, isSubmitting, setActiveTab, isViewOnlyMode, selectedEvalId, }: { handleSubmit: (action: "save" | "complete") => void; isSubmitting: boolean; setActiveTab: (tabId: string) => void; isViewOnlyMode: boolean; selectedEvalId?: number | null; }) => {
  return (
    <SectionTab headerNames={["Walk"]} handleSubmit={handleSubmit} isSubmitting={isSubmitting} setActiveTab={setActiveTab} nextTab="vpe-mhe" currentTab="walk" isViewOnlyMode={isViewOnlyMode} selectedEvalId={selectedEvalId} />
  );
};

// New section tabs for all 9 sections
const VpeMheTab = ({ handleSubmit, isSubmitting, setActiveTab, isViewOnlyMode, selectedEvalId }: any) => (
  <SectionTab headerNames={["VP Equipment/MHE/Supplies/Pre-Shift"]} handleSubmit={handleSubmit} isSubmitting={isSubmitting} setActiveTab={setActiveTab} nextTab="yard-gate" currentTab="vpe-mhe" isViewOnlyMode={isViewOnlyMode} selectedEvalId={selectedEvalId} />
);

const YardGateTab = ({ handleSubmit, isSubmitting, setActiveTab, isViewOnlyMode, selectedEvalId }: any) => (
  <SectionTab headerNames={["Yard/Gate"]} handleSubmit={handleSubmit} isSubmitting={isSubmitting} setActiveTab={setActiveTab} nextTab="layer-redzone" currentTab="yard-gate" isViewOnlyMode={isViewOnlyMode} selectedEvalId={selectedEvalId} />
);

const LayerRedzoneTab = ({ handleSubmit, isSubmitting, setActiveTab, isViewOnlyMode, selectedEvalId }: any) => (
  <SectionTab headerNames={["Layer/Red Zone/Docks"]} handleSubmit={handleSubmit} isSubmitting={isSubmitting} setActiveTab={setActiveTab} nextTab="pick-area" currentTab="layer-redzone" isViewOnlyMode={isViewOnlyMode} selectedEvalId={selectedEvalId} />
);

const PickAreaTab = ({ handleSubmit, isSubmitting, setActiveTab, isViewOnlyMode, selectedEvalId }: any) => (
  <SectionTab headerNames={["Pick Area"]} handleSubmit={handleSubmit} isSubmitting={isSubmitting} setActiveTab={setActiveTab} nextTab="deep-reserve" currentTab="pick-area" isViewOnlyMode={isViewOnlyMode} selectedEvalId={selectedEvalId} />
);

const DeepReserveTab = ({ handleSubmit, isSubmitting, setActiveTab, isViewOnlyMode, selectedEvalId }: any) => (
  <SectionTab headerNames={["Deep Reserve/Chilled/Repack"]} handleSubmit={handleSubmit} isSubmitting={isSubmitting} setActiveTab={setActiveTab} nextTab="regulatory" currentTab="deep-reserve" isViewOnlyMode={isViewOnlyMode} selectedEvalId={selectedEvalId} />
);

const RegulatoryTab = ({ handleSubmit, isSubmitting, setActiveTab, isViewOnlyMode, selectedEvalId }: any) => (
  <SectionTab headerNames={["Regulatory/Documentation"]} handleSubmit={handleSubmit} isSubmitting={isSubmitting} setActiveTab={setActiveTab} nextTab="lighthouse" currentTab="regulatory" isViewOnlyMode={isViewOnlyMode} selectedEvalId={selectedEvalId} />
);

const LighthouseTab = ({ handleSubmit, isSubmitting, setActiveTab, isViewOnlyMode, selectedEvalId }: any) => (
  <SectionTab headerNames={["Lighthouse"]} handleSubmit={handleSubmit} isSubmitting={isSubmitting} setActiveTab={setActiveTab} nextTab="" currentTab="lighthouse" isViewOnlyMode={isViewOnlyMode} selectedEvalId={selectedEvalId} />
);

const PreWorkTab = ({ handleRowClick, isViewOnlyMode = false, selectedEvalId }: { handleRowClick: (rowData: any) => void; isViewOnlyMode?: boolean; selectedEvalId?: number | null }) => {
  const { colors: pwColors, resolvedTheme: pwResolvedTheme } = useTheme();
  const pwIsDark = pwResolvedTheme === "dark";
  const dispatch = useDispatch();
  const preWorkData = useSelector(selectPreWorkData);
  const preWorkAnswers = useSelector(selectPreWorkAnswers);
  const preWorkTargets = useSelector(selectPreWorkTargets);
  const actionPlans = useSelector(selectSiteEvalActionPlans);
  
  // Action plan overlay state - add this for the action plan functionality
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayTitle, setOverlayTitle] = useState('');
  const [currentOverlayId, setCurrentOverlayId] = useState<string | null>(null);
  const [currentQuestionTitle, setCurrentQuestionTitle] = useState<string>('');
  const [actionPlanText, setActionPlanText] = useState('');
  const [selectedSupervisor, setSelectedSupervisor] = useState('');

  // Handle overlay save - integrated with main save logic
  const handleOverlaySave = (actionPlan: string, supervisor: string) => {
    if (isViewOnlyMode) return;
    if (!currentOverlayId) return;
    
    // Store action plan data in Redux
    const actionPlanData = {
      id: Date.now().toString(),
      localId: Date.now(),
      itemId: currentOverlayId,
      relatedId: currentOverlayId,
      title: overlayTitle || 'Site Evaluation Action Plan',
      actionPlan,
      supervisor,
      assignee: supervisor,
      source: 'Site Evaluation',
      type: null,
      priority: null,
      startDate: null,
      targetDate: null,
      notes: '',
      frontlineInvolvement: false,
      employee: null,
      employeeName: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isReference: false,
      status: 'Active'
    };
    
    // Dispatch to Redux - will update if exists or add new
    dispatch(updateSiteEvalActionPlan(actionPlanData));
    console.log('Action plan saved successfully to Redux:', actionPlanData);
    
    // Close overlay
    setOverlayVisible(false);
    setCurrentOverlayId(null);
    setCurrentQuestionTitle('');
  };

  // Handle overlay dismiss
  const handleOverlayDismiss = () => {
    setOverlayVisible(false);
    setCurrentOverlayId(null);
    setCurrentQuestionTitle('');
  };

  // Use preWorkAnswers if available, otherwise fallback to preWorkData
  const rawDisplayData = preWorkAnswers.length > 0 ? preWorkAnswers : preWorkData;
  
  // Filter to only show DATA rows, exclude HEADER and SubHeader
  // If Section_Type field doesn't exist (mapped data), show all rows
  const displayData = rawDisplayData.filter((item: any) => {
    const sectionType = item.Section_Type || item.section_type || item.sectionType;
    // If no Section_Type field exists, include the item (it's already filtered/mapped data)
    if (!sectionType) return true;
    // Otherwise only include DATA rows
    return sectionType === 'DATA';
  });

  // Determine device type
  const windowWidth = Dimensions.get('window').width;
  const mobile = isMobileView() && windowWidth < 768;
  const iPad = isIPadView();

  const handleTargetChange = (id: string, newTarget: 'on-target' | 'off-target') => {
    const currentTarget = preWorkTargets[id];
    // Toggle off if clicking the same button, otherwise set new target
    if (currentTarget === newTarget) {
      dispatch(setPreWorkTarget({ id, target: null }));
    } else {
      dispatch(setPreWorkTarget({ id, target: newTarget }));
    }
  };

  // For mobile ONLY, show card-based layout instead of table
  if (mobile && !iPad) {
    return (
      <ScrollView style={styles.mobilePreWorkContainer} showsVerticalScrollIndicator={false}>
        {displayData.map((item: any, index: number) => {
          const hasActionPlan = checkIfActionPlanExistsInRedux(actionPlans, item.id, selectedEvalId);
          const storedPlan = getStoredActionPlanFromRedux(actionPlans, item.id, selectedEvalId);
          // Mirror the desktop auto-target logic: if no manual selection, derive from data
          const priorVisitValue = item.priorVisit || item.Prior_Visit || 0;
          const ytdValue = item.ytd || item.YTD || 0;
          const autoTarget = priorVisitValue === ytdValue ? 'on-target' : 'off-target';
          const currentTarget = preWorkTargets[item.id] ?? autoTarget;

          return (
            <View key={item.id || index} style={[styles.preWorkCard, pwIsDark && { backgroundColor: pwColors.background.elevated }]}>
              {/* Header with metric name */}
              <View style={styles.preWorkCardHeader}>
                <Text style={styles.preWorkCardHeaderText}>
                  {item.review || item.metricName || `Metric ${index + 1}`}
                </Text>
              </View>

              {/* Content area with data points */}
              <View style={styles.preWorkCardContent}>
                <View style={styles.preWorkDataSection}>
                  <Text style={[styles.preWorkDataLabel, pwIsDark && { color: pwColors.text.primary }]}>
                    <Text style={[styles.preWorkDataLabelBold, pwIsDark && { color: pwColors.text.primary }]}>YTD Prior Visit: </Text>
                    {item.priorVisit || item.Prior_Visit || '--'}
                  </Text>
                  <Text style={[styles.preWorkDataLabel, pwIsDark && { color: pwColors.text.primary }]}>
                    <Text style={[styles.preWorkDataLabelBold, pwIsDark && { color: pwColors.text.primary }]}>LCP: </Text>
                    {item.lcp || item.LCP || '--'}
                  </Text>
                  <Text style={[styles.preWorkDataLabel, pwIsDark && { color: pwColors.text.primary }]}>
                    <Text style={[styles.preWorkDataLabelBold, pwIsDark && { color: pwColors.text.primary }]}>YTD: </Text>
                    {item.ytd || item.YTD || '--'}
                  </Text>
                </View>

                <View style={[styles.preWorkActionsSection, pwIsDark && { borderTopColor: pwColors.border.divider }]}>
                  {/* Button group for On Target / Off Target (read-only, auto-determined) */}
                  <View style={[styles.preWorkButtonGroup, pwIsDark && { borderColor: pwColors.border.default, backgroundColor: pwColors.border.default }]}>
                    <View
                      style={[
                        styles.preWorkButton,
                        styles.preWorkButtonLeft,
                        pwIsDark && { backgroundColor: pwColors.button.secondaryBackground, borderRightColor: pwColors.border.default },
                        currentTarget === 'on-target' && styles.preWorkButtonActive,
                      ]}
                    >
                      <Text style={[
                        styles.preWorkButtonText,
                        pwIsDark && { color: pwColors.button.secondaryText },
                        currentTarget === 'on-target' && styles.preWorkButtonActiveText,
                      ]}>
                        On Target
                      </Text>
                    </View>

                    <View
                      style={[
                        styles.preWorkButton,
                        styles.preWorkButtonRight,
                        pwIsDark && { backgroundColor: pwColors.button.secondaryBackground },
                        currentTarget === 'off-target' && styles.preWorkButtonActive,
                      ]}
                    >
                      <Text style={[
                        styles.preWorkButtonText,
                        pwIsDark && { color: pwColors.button.secondaryText },
                        currentTarget === 'off-target' && styles.preWorkButtonActiveText,
                      ]}>
                        Off Target
                      </Text>
                    </View>
                  </View>

                  {/* Action Plan link — only shown when off-target */}
                  {currentTarget === 'off-target' && <TouchableOpacity
                    style={styles.preWorkActionPlanLink}
                    onPress={() => {
                      if (isViewOnlyMode) return;
                      const uniqueKey = selectedEvalId ? `${selectedEvalId}_${item.id}` : item.id;
                      setCurrentOverlayId(uniqueKey);
                      if (hasActionPlan && storedPlan) {
                        setCurrentQuestionTitle(item.SiteEvaluationaForm || item.review || storedPlan.title || 'Edit Action Plan');
                        setOverlayTitle(storedPlan.title || 'Edit Action Plan');
                        setActionPlanText(storedPlan.actionPlan || '');
                        setSelectedSupervisor(storedPlan.supervisor || storedPlan.assignee || '');
                      } else {
                        setCurrentQuestionTitle(item.SiteEvaluationaForm || item.review || 'Create Action Plan');
                        setOverlayTitle('Create Action Plan');
                        setActionPlanText('');
                        setSelectedSupervisor('');
                      }
                      setOverlayVisible(true);
                    }}
                  >
                    <MaterialIcons
                      name={hasActionPlan ? 'edit' : 'add'}
                      size={18}
                      color={pwIsDark ? pwColors.brand.primary : "#005CBC"}
                    />
                    <Text style={[styles.preWorkActionPlanText, pwIsDark && { color: pwColors.brand.primary }]}>
                      {hasActionPlan ? 'Action Plan' : 'Add a Action Plan'}
                    </Text>
                    {hasActionPlan && storedPlan && (
                      <DLOTooltip
                        content={`Supervisor: ${formatUserName(storedPlan.supervisor || storedPlan.assignee || 'N/A')}`}
                        title={`Action Plan: ${storedPlan.actionPlan || ''}`}
                        position="top"
                        trigger="hover"
                        maxWidth={260}
                      >
                        <MaterialIcons
                          name="info"
                          size={18}
                          color="#005CBC"
                          style={styles.preWorkInfoIcon}
                        />
                      </DLOTooltip>
                    )}
                  </TouchableOpacity>}
                </View>
              </View>
            </View>
          );
        })}

        {/* Action Plan Overlay for PreWorkTab mobile */}
        <SiteEvalActionPlanOverlay
          visible={overlayVisible}
          onDismiss={handleOverlayDismiss}
          title={overlayTitle}
          questionId={currentOverlayId || ''}
          questionTitle={currentQuestionTitle}
          initialActionPlan={actionPlanText}
          initialSupervisor={selectedSupervisor}
          onSave={handleOverlaySave}
          isViewOnlyMode={isViewOnlyMode}
        />
      </ScrollView>
    );
  }

  // For desktop/tablet, keep the existing table layout
  const preWorkColumns = [
    { columnKey: "review", header: "Review" },
    { columnKey: "priorVisit", header: " YTD Prior Visit" },
    { columnKey: "lcp", header: "LCP" },
    { columnKey: "ytd", header: "YTD" },
    {
      columnKey: "target",
      header: "Target",
      width: 200,
      renderCell: (item: any) => {
        // Automatically determine target based on YTD Prior Visit === YTD
        const priorVisitValue = item.priorVisit || item.Prior_Visit || 0;
        const ytdValue = item.ytd || item.YTD || 0;
        const autoTarget = priorVisitValue === ytdValue ? 'on-target' : 'off-target';
        
        return (
          <View style={{
            flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 1, borderRadius: 4,
            borderWidth: 1,
            borderColor: pwIsDark ? pwColors.border.default : "#D7E5F8",
            overflow: "hidden",
          }}>
            <View
              style={[
                styles.targetButton,
                styles.targetButtonLeft,
                pwIsDark && { backgroundColor: pwColors.button.secondaryBackground },
                autoTarget === 'on-target' && styles.targetButtonActive,
              ]}
            >
              <Text
                style={[
                  styles.targetButtonText,
                  pwIsDark && { color: pwColors.button.secondaryText },
                  autoTarget === 'on-target' && styles.targetButtonActiveText,
                ]}
              >
                On Target
              </Text>
            </View>
            <View style={[styles.dividerSmall, pwIsDark && { backgroundColor: pwColors.border.default }]} />
            <View
              style={[
                styles.targetButton,
                styles.targetButtonRight,
                pwIsDark && { backgroundColor: pwColors.button.secondaryBackground },
                autoTarget === 'off-target' && styles.targetButtonActive,
              ]}
            >
              <Text
                style={[
                  styles.targetButtonText,
                  pwIsDark && { color: pwColors.button.secondaryText },
                  autoTarget === 'off-target' && styles.targetButtonActiveText,
                ]}
              >
                Off Target
              </Text>
            </View>
          </View>
        );
      }
    },
    { 
      columnKey: "actionPlan", 
      header: "Action Plan",
      width: 190,
      renderCell: (item: any) => {
        // Only show action plan controls when the row is off-target
        const priorVisitValue = item.priorVisit || item.Prior_Visit || 0;
        const ytdValue = item.ytd || item.YTD || 0;
        const autoTarget = priorVisitValue === ytdValue ? 'on-target' : 'off-target';
        const currentTarget = preWorkTargets[item.id] ?? autoTarget;
        if (currentTarget !== 'off-target') return null;

        // Check if action plan exists for this item using Redux
        const hasActionPlan = checkIfActionPlanExistsInRedux(actionPlans, item.id, selectedEvalId);
        const storedPlan = getStoredActionPlanFromRedux(actionPlans, item.id, selectedEvalId);
        
        if (hasActionPlan && storedPlan) {
          // Show Figma design with edit icon and "Action Plan" text
          return (
            <TouchableOpacity
              style={styles.actionPlanCellCompleted}
              onPress={() => {
                if (isViewOnlyMode) return;
                console.log('Edit Action Plan clicked for:', item.id);
                // Open overlay in edit mode with existing data
                const uniqueKey = selectedEvalId ? `${selectedEvalId}_${item.id}` : item.id;
                setCurrentOverlayId(uniqueKey);
                setCurrentQuestionTitle(item.SiteEvaluationaForm || item.review || storedPlan.title || 'Edit Action Plan');
                setOverlayTitle(storedPlan.title || 'Edit Action Plan');
                setActionPlanText(storedPlan.actionPlan || '');
                setSelectedSupervisor(storedPlan.supervisor || storedPlan.assignee || '');
                setOverlayVisible(true);
              }}
            >
              <EditIcon />
              <Text style={[styles.actionPlanTextCompleted, pwIsDark && { color: pwColors.brand.primary }]}>Action Plan</Text>
              <DLOTooltip
                content={`Supervisor: ${formatUserName(storedPlan.supervisor || storedPlan.assignee || 'N/A')}`}
                title={`Action Plan: ${storedPlan.actionPlan || ''}`}
                position="top"
                trigger="hover"
                maxWidth={260}
              >
                <InfoIcon />
              </DLOTooltip>
            </TouchableOpacity>
          );
        }
        
        // Show original "Add a Action Plan" design
        return (
          <TouchableOpacity
            style={styles.actionPlanCell}
            onPress={() => {
              if (isViewOnlyMode) return;
              console.log('Add Action Plan clicked for:', item.id);
              // Open overlay in create mode
              const uniqueKey = selectedEvalId ? `${selectedEvalId}_${item.id}` : item.id;
              setCurrentOverlayId(uniqueKey);
              setCurrentQuestionTitle(item.SiteEvaluationaForm || item.review || 'Create Action Plan');
              setOverlayTitle('Create Action Plan');
              setActionPlanText('');
              setSelectedSupervisor('');
              setOverlayVisible(true);
            }}
          >
            <AddIcon />
            <Text style={[styles.actionPlanText, pwIsDark && { color: pwColors.brand.primary }]}>Add a Action Plan</Text>
          </TouchableOpacity>
        );
      }
    },
  ];

  return (
    <View style={[styles.tableCard, pwIsDark && { backgroundColor: pwColors.background.elevated }]}>
      <SiteEvalDataTable
        data={displayData}
        maxRecordsToShow={100}
        columns={preWorkColumns}
        tableHeader={""}
        tableDescription={""}
        onRowClick={handleRowClick}
      />
      
      {/* Action Plan Overlay for PreWorkTab */}
      <SiteEvalActionPlanOverlay
        visible={overlayVisible}
        onDismiss={handleOverlayDismiss}
        title={overlayTitle}
        questionId={currentOverlayId || ''}
        questionTitle={currentQuestionTitle}
        initialActionPlan={actionPlanText}
        initialSupervisor={selectedSupervisor}
        onSave={handleOverlaySave}
        isViewOnlyMode={isViewOnlyMode}
      />
    </View>
  );
};

const SiteEvalRedirectScreen: React.FC<SiteEvalRedirectScreenProps> = ({
  onBackPress,
  onHomePress,
  showStatusMessage,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [activeTab, setActiveTab] = useState("pre-work");
  const dispatch: AppDispatch = useDispatch();

  // Get the selected site evaluation record (full object) early to check if it's a new evaluation
  const selectedSiteEvaluation = useSelector(
    (state: any) => state.siteEvaluation.selectedSiteEvaluation
  );
  
  // Get action plans from Redux
  const actionPlans = useSelector(selectSiteEvalActionPlans);

  // Clear Redux and reset state when starting a NEW evaluation (no selectedSiteEvaluation)
  React.useEffect(() => {
    if (!selectedSiteEvaluation) {
      // This is a new evaluation - clear Redux action plans
      dispatch(clearSiteEvalActionPlans());
      // Reset Redux state to ensure clean start
      dispatch(resetSiteEvalRedirect());
    }
  }, [selectedSiteEvaluation, dispatch]);

  // Reset Redux state when component unmounts
  React.useEffect(() => {
    return () => {
      dispatch(resetSiteEvalRedirect());
    };
  }, [dispatch]);

  // --- Shared handleSubmit and preparePayload for both WalkTab and CloseOutlistTab ---
  const closeOutData = useSelector(selectCloseOutData) as any[];
  const closeOutAnswers = useSelector(selectCloseOutAnswers) as {
    [id: string]: { partner?: string };
  };
  const preWorkData = useSelector(selectPreWorkData) as any[];
  const workData = useSelector(selectWorkData) as any[];
  const walkAnswers = useSelector(selectWalkAnswers) as any[];
  const COEUsers = useSelector((state: any) => state.appData.COEUsers);
  const FieldUsers = useSelector((state: any) => state.appData.FieldUsers);
  const selectedLocation = useSelector(
    (state: any) => state.appData.selectedLocation
  );

  // Safe ref pattern: redirect back to Site Evaluation dashboard on location change
  const initialLocationIdRef = useRef(selectedLocation?.id);
  const onBackPressRef = useRef(onBackPress);
  useEffect(() => { onBackPressRef.current = onBackPress; }, [onBackPress]);
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    const initialId = initialLocationIdRef.current;
    if (
      !hasRedirectedRef.current &&
      initialId &&
      selectedLocation?.id &&
      initialId !== selectedLocation.id
    ) {
      hasRedirectedRef.current = true;
      onBackPressRef.current();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id]);

  const periodWeek = useSelector((state: any) => state.appData.periodWeek);
  const user = useSelector((state: any) => state.auth.user);
  // Get userInfo from appData which includes GPID and email from location-with-access API
  const userInfo = useSelector((state: any) => state.appData.userInfo);
  const actionPlanSessionIdFromRedux = useSelector(selectActionPlanSessionId);
  // selectedSiteEvaluation is already defined above in the useEffect
  const selectedSiteEvaluationId = selectedSiteEvaluation?.id;
  const status = selectedSiteEvaluation?.EvaluationStatus || "";
  // Determine if the form should be view-only
  const isViewOnlyMode = status === "Completed";
  // Use userInfo from location-with-access API for email and GPID
  const userEmail = userInfo?.email || user?.email || user?.username || "";
  const userGPID = userInfo?.GPID || user?.gpid || "";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const { showToast } = require("../../components/LhToastContainer").useToast();
  const navigateBack = onBackPress;

  // Confirmation dialog state for navigating away from Pre-Work tab
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmNavigationTarget, setConfirmNavigationTarget] = useState<'home' | 'back'>('home');

  const handleConfirmNavigation = () => {
    // Reset redirect state and then navigate back to Site Evaluation home
    dispatch(resetSiteEvalRedirect());
    setShowConfirmDialog(false);
    if (onBackPress) onBackPress();
  };

  const handleCancelNavigation = () => {
    setShowConfirmDialog(false);
  };

  // Helper function to validate if all questions in a specific section are answered
  const validateSectionCompletion = (tabId: string): boolean => {
    // Pre-work tab doesn't need validation (it's not a survey)
    if (tabId === 'pre-work') {
      return true;
    }

    // Map tab IDs to their respective header names
    const tabToHeaderMap: Record<string, string[]> = {
      'walk': ['Walk'],
      'vpe-mhe': ['VP Equipment/MHE/Supplies/Pre-Shift'],
      'yard-gate': ['Yard/Gate'],
      'layer-redzone': ['Layer/Red Zone/Docks'],
      'pick-area': ['Pick Area'],
      'deep-reserve': ['Deep Reserve/Chilled/Repack'],
      'regulatory': ['Regulatory/Documentation'],
      'lighthouse': ['Lighthouse'],
    };

    const headerNames = tabToHeaderMap[tabId];
    if (!headerNames) {
      return true; // If no mapping found, allow navigation
    }

    // Get section data for the current tab
    const sectionRows = getSectionData(workData || [], headerNames);
    
    // Filter only radio question rows (exclude headers and toggle-only rows)
    const radioRows = sectionRows.filter(
      (row: any) => !row.isHeader && !row.toggleOnly
    );

    // Check if all radio questions have answers
    const allAnswered = radioRows.every((row: any) => {
      const ans = (walkAnswers || []).find(
        (a: any) => String(a.id) === String(row.id)
      );
      return (
        ans &&
        ans.answer !== undefined &&
        ans.answer !== null &&
        ans.answer !== ""
      );
    });

    return allAnswered;
  };

  // Handle tab change with validation
  const handleTabChange = (newTabId: string) => {
    // In view-only mode, allow free navigation
    if (isViewOnlyMode) {
      setActiveTab(newTabId);
      return;
    }

    // Get the order of tabs
    const tabOrder = [
      'pre-work',
      'walk',
      'vpe-mhe',
      'yard-gate',
      'layer-redzone',
      'pick-area',
      'deep-reserve',
      'regulatory',
      'lighthouse',
    ];

    const currentIndex = tabOrder.indexOf(activeTab);
    const newIndex = tabOrder.indexOf(newTabId);

    // Allow backward navigation without validation
    if (newIndex < currentIndex) {
      setActiveTab(newTabId);
      return;
    }

    // For forward navigation, validate current tab before allowing navigation
    if (newIndex > currentIndex && !validateSectionCompletion(activeTab)) {
      showToast({
        header: "Incomplete Section",
        subtext: "All questions in the current section must be answered before moving to the next section.",
        variant: "warning",
        position: "top",
      });
      return;
    }

    // If validation passes or moving backward, allow navigation
    setActiveTab(newTabId);
  };

  // --- Enhanced Payload Preparation ---
  function prepareEnhancedPayload(action: "save" | "complete", isNewRecord: boolean = false) {
    // Helper to map answer string to response number
    const mapAnswerToResponse = (answer: any) => {
      if (answer === "yes" || answer === 1) return 1;
      if (answer === "no" || answer === 2) return 2;
      if (answer === "na" || answer === 3) return 3;
      return undefined;
    };

    // Helper function to find user details by email or GPID
    const findUserDetails = (identifier: string) => {
      if (!identifier) return null;
      
      const allUsers = [...(COEUsers || []), ...(FieldUsers || [])];
      
      // Try to find by email first
      let user = allUsers.find((u: any) => 
        u.UserEmail && u.UserEmail.toLowerCase() === identifier.toLowerCase()
      );
      
      // If not found, try by GPID
      if (!user) {
        user = allUsers.find((u: any) => u.GPID === identifier);
      }
      
      if (user) {
        return {
          gpid: user.GPID || '',
          email: user.UserEmail || '',
          role: user.Role || '',
          roleId: user.RoleId || 0,
          name: user.Name || user.UserEmail || ''
        };
      }
      
      return null;
    };

    // Prepare prework data
    const prework = preWorkData
      .filter((item: any) => item.priorVisit !== undefined || item.lcp !== undefined || item.ytd !== undefined)
      .map((item: any) => ({
        question_id: Number(item.id),
        prior_visit: item.priorVisit,
        lcp: item.lcp,
        ytd: item.ytd
      }));

    // Helper: resolve section name by walking up the parentId chain in workData.
    // This is robust to any question ID — only the ancestor IDs need to fall in known ranges.
    const idToSection = (id: number, visited = new Set<number>()): string | null => {
      if (visited.has(id)) return null; // guard against circular refs
      visited.add(id);

      if (id >= 1000 && id < 2000) return 'walk';
      if (id >= 2000 && id < 3000) return 'vpe_mhe';
      if (id >= 3000 && id < 4000) return 'yard_gate';
      if (id >= 4000 && id < 5000) return 'layer_redzone';
      if (id >= 5000 && id < 6000) return 'pick_area';
      if (id >= 6000 && id < 7000) return 'deep_reserve';
      if (id >= 7000 && id < 8000) return 'regulatory';
      if (id >= 8000 && id < 9000) return 'lighthouse';

      // ID not in a known range — look up its parent and recurse
      const row = (workData || []).find((r: any) => Number(r.id) === id);
      if (!row?.parentId) return null;
      return idToSection(Number(row.parentId), visited);
    };

    // Group walkAnswers by section using parentId-based resolution
    const sectionGroups: { [key: string]: any[] } = {
      walk: [],
      vpe_mhe: [],
      yard_gate: [],
      layer_redzone: [],
      pick_area: [],
      deep_reserve: [],
      regulatory: [],
      lighthouse: []
    };

    walkAnswers
      .filter((ans: any) => ans.answer !== undefined && ans.answer !== null)
      .forEach((ans: any) => {
        const questionId = Number(ans.id);
        const response = mapAnswerToResponse(ans.answer);

        if (response === undefined) return;

        const section = idToSection(questionId);
        if (!section) return; // unresolvable — skip

        const questionData = {
          question_id: questionId,
          response: response,
          // Include action plan and supervisor data if present
          ...(ans.actionPlan && { action_plan: ans.actionPlan }),
          ...(ans.supervisor && { supervisor: ans.supervisor }),
          ...(ans.comment && { comment: ans.comment }),
        };

        sectionGroups[section].push(questionData);
      });

    // Retrieve action_plan_session_id from Redux
    const actionPlanSessionId: string | null = actionPlanSessionIdFromRedux || null;
    console.log('[prepareEnhancedPayload] Using action_plan_session_id:', actionPlanSessionId);

    // Prepare enhanced payload
    const payload: any = {};
    
    // For new records (POST), include creation fields
    if (isNewRecord) {
      payload.location = selectedLocation?.displayText || selectedLocation?.name || '';
      payload.created_by_gpid = userGPID || '';
      payload.created_by_email = userEmail || '';
      payload.created_by_role = userInfo?.roleName || '';
      payload.created_by_role_id = userInfo?.roleId || 0;
      payload.status = action == "complete" ? "completed" : "in_progress";
    } else {
      // For existing records (PUT), include update fields
      payload.updated_by_gpid = userGPID || '';
      payload.updated_by_email = userEmail || '';
      payload.updated_by_role = userInfo?.roleName ;
      payload.updated_by_role_id = userInfo?.roleId ;
      payload.status = action == "complete" ? "completed" : "in_progress";
    }

    // Add sections with data (always include empty arrays for POST)
    payload.prework = prework.length > 0 ? prework : (isNewRecord ? [] : undefined);
    payload.walk = sectionGroups.walk.length > 0 ? sectionGroups.walk : (isNewRecord ? [] : undefined);
    payload.vpe_mhe = sectionGroups.vpe_mhe.length > 0 ? sectionGroups.vpe_mhe : (isNewRecord ? [] : undefined);
    payload.yard_gate = sectionGroups.yard_gate.length > 0 ? sectionGroups.yard_gate : (isNewRecord ? [] : undefined);
    payload.layer_redzone = sectionGroups.layer_redzone.length > 0 ? sectionGroups.layer_redzone : (isNewRecord ? [] : undefined);
    payload.pick_area = sectionGroups.pick_area.length > 0 ? sectionGroups.pick_area : (isNewRecord ? [] : undefined);
    payload.deep_reserve = sectionGroups.deep_reserve.length > 0 ? sectionGroups.deep_reserve : (isNewRecord ? [] : undefined);
    payload.regulatory = sectionGroups.regulatory.length > 0 ? sectionGroups.regulatory : (isNewRecord ? [] : undefined);
    payload.lighthouse = sectionGroups.lighthouse.length > 0 ? sectionGroups.lighthouse : (isNewRecord ? [] : undefined);
    
    // Add action_plan_session_id instead of action_plans array
    if (actionPlanSessionId) {
      payload.action_plan_session_id = actionPlanSessionId;
    }
    
    // Clean up undefined values for cleaner payload
    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined) delete payload[key];
    });

    return payload;
  }

  // --- Legacy Payload Preparation (kept for backward compatibility) ---
  function preparePayload(action: "save" | "complete") {
    // Pre_Work_Data
    const Pre_Work_Data = preWorkData.map((item: any) => ({
      Data_Id: item.id || 0,
      Review: item.review || "",
      Prior_Visit: item.priorVisit || "",
      LCP: item.lcp || "",
      YTD: item.ytd || "",
    }));
    // Walk_Data: Only include answers that do NOT have supervisor or actionPlan (those go in Walk_Supervisor_Review)
    const Walk_Data = walkAnswers
      .filter(
        (ans: any) =>
          ans.answer !== undefined &&
          ans.answer !== null &&
          !ans.supervisor &&
          !ans.actionPlan
      )
      .map((ans: any) => {
        // Ensure id is a number for comparison
        const ansIdNum = typeof ans.id === "number" ? ans.id : Number(ans.id);
        const row =
          workData.find((row: any) => Number(row.id) === ansIdNum) || {};
        // Map answer to 1 (yes), 2 (no), 3 (na)
        let responseValue = "";
        if (ans.answer === "yes" || ans.answer === 1) responseValue = "1";
        else if (ans.answer === "no" || ans.answer === 2) responseValue = "2";
        else if (ans.answer === "na" || ans.answer === 3) responseValue = "3";
        return {
          Data_Id: ansIdNum || 0,
          Response: responseValue,
          Section_Type: row.Section_Type || "Data",
          Section_Value: row.SiteEvaluationaForm || "",
          Parent_Id: row.parentId || 0,
          Sort_Order: row.sortOrder || 0,
        };
      });
    // Walk_Supervisor_Review
    const Walk_Supervisor_Review = walkAnswers
      .filter((a: any) => a.actionPlan || a.supervisor)
      .map((a: any) => ({
        Data_Id: a.id || 0,
        Supervisor: a.supervisor || "",
        Comment: a.actionPlan || "",
      }));
    // Close_Out_Review
    const Close_Out_Review = Object.values(closeOutAnswers).map((a: any) => ({
      Partner: a.partner || "",
    }));

    // Main payload
    let payload: any = {
      oneSubmission: false, // default for new
      location: selectedLocation?.displayText || selectedLocation?.name || "",
      period: periodWeek?.data?.period || 0,
      week: periodWeek?.data?.week || 0,
      created_by: userEmail || "",
      status: action === "complete" ? "Completed" : "In Progress",
      Pre_Work_Data,
      Walk_Data,
      Walk_Supervisor_Review,
      Close_Out_Review,
    };
    // If editing existing, set oneSubmission true and add selectedRecord/site-evaluation_id
    if (
      selectedSiteEvaluationId !== undefined &&
      selectedSiteEvaluationId !== null
    ) {
      payload.oneSubmission = true;
      payload.selectedRecord = { Id: selectedSiteEvaluationId };
    }
    return payload;
  }

  // --- Enhanced API Call ---
  async function handleSubmit(action: "save" | "complete") {
    if (isViewOnlyMode) {
      showToast({
        header: "Read Only",
        subtext: "This site evaluation is completed and cannot be edited.",
        variant: "info",
        position: "top",
      });
      return;
    }
    // Validation: Only for 'complete' action, require all radio questions in WalkTab to be answered
    if (action === "complete") {
      // Find all Data rows in workData (radio questions)
      const radioRows = (workData || []).filter(
        (row: any) => !row.isHeader && !row.toggleOnly
      );
      // For each radio row, check if there is a walkAnswer with a defined answer
      const allAnswered = radioRows.every((row: any) => {
        const ans = (walkAnswers || []).find(
          (a: any) => String(a.id) === String(row.id)
        );
        return (
          ans &&
          ans.answer !== undefined &&
          ans.answer !== null &&
          ans.answer !== ""
        );
      });
      if (!allAnswered) {
        showToast({
          header: "Incomplete Form",
          subtext: "All walk questions must be answered before completing.",
          variant: "warning",
          position: "top",
        });
        return;
      }
    }
    setIsSubmitting(true);
    try {
      const { createApiClient, apiClient, getLocalTimestamp, getTimezoneOffsetString } = require("../../api/apiClient");
      const api = createApiClient();
      
      // Use enhanced API for both creating and updating
      if (selectedSiteEvaluationId !== undefined && selectedSiteEvaluationId !== null) {
        // Updating existing record using enhanced API
        // For PUT: include both clientTimestamp and clientTimezone as query params
        const clientTimestamp = getLocalTimestamp();
        const clientTimezone = getTimezoneOffsetString();
        const payload = prepareEnhancedPayload(action, false);
        console.log("Submitting enhanced PUT payload:", payload);
        await apiClient(api, `/site-evaluation-enhanced/${selectedSiteEvaluationId}?clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`, "PUT", payload);
      } else {
        // Creating new record using enhanced POST API
        // For POST: clientTimestamp is added automatically by apiClient interceptor, only add clientTimezone
        const clientTimezone = getTimezoneOffsetString();
        const payload = prepareEnhancedPayload(action, true);
        console.log("Submitting enhanced POST payload:", payload);
        await apiClient(api, `/site-evaluation-enhanced?clientTimezone=${encodeURIComponent(clientTimezone)}`, "POST", payload);
        
        // Clear Redux action plans and session ID after successful submission
        dispatch(clearSiteEvalActionPlans());
      }
      
      if (showStatusMessage) {
        showStatusMessage(
          "success",
          action === "complete"
            ? "SiteEvaluation completed successfully!"
            : "SiteEvaluation saved successfully!"
        );
      } else {
        showToast({
          header: "Success",
          subtext:
            action === "complete"
              ? "SiteEvaluation completed successfully!"
              : "SiteEvaluation saved successfully!",
          variant: "success",
        });
      }
      navigateBack();
    } catch (err: any) {
      if (showStatusMessage) {
        showStatusMessage("error", err?.message || "Failed to submit.");
      } else {
        showToast({
          header: "Error",
          subtext: err?.message || "Failed to submit.",
          variant: "error",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // --- PDF Export Implementation ---
  async function handlePdfExport() {
    if (!selectedSiteEvaluationId || !userEmail) {
      showToast({
        header: "PDF Export Error",
        subtext: "Missing record or user email.",
        variant: "error",
      });
      return;
    }
    setIsPdfExporting(true);
    try {
      const { createApiClient, apiClient } = require("../../api/apiClient");
      const api = createApiClient();
      // Compose subject line and module name
      const subjectLine = `Site Evaluation Report - ${selectedSiteEvaluationId}`;
      const inspectionModule = "site_evaluation";
      const schemaVersion = "new";
      // Call the API (GET for consistency with leadership walk)
      const response = await apiClient(
        api,
        `/notifications/email/pdf?emailTo=${encodeURIComponent(
          userEmail
        )}&inspection_module=${inspectionModule}&id=${selectedSiteEvaluationId}&subject=${encodeURIComponent(
          subjectLine
        )}&location=${encodeURIComponent(selectedLocation.displayText)}&schema_version=${schemaVersion}`,
        "GET"
      );
      if (response && response.message) {
        showToast({
          header: "Email Notification",
          subtext: response.message,
          variant: response.messageId ? "success" : "error",
        });
        console.log("PDF export success:", response.message);
      } else {
        showToast({
          header: "Email Error",
          subtext: "Failed to send PDF export email",
          variant: "error",
        });
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      showToast({
        header: "Email Error",
        subtext: "Failed to export PDF",
        variant: "error",
      });
    } finally {
      setIsPdfExporting(false);
    }
  }

  // --- CloseOutlistTab definition ---
  function CloseOutlistTab({
    handleSubmit,
    isSubmitting,
    handlePdfExport,
    isViewOnlyMode,
  }: {
    handleSubmit: (action: "save" | "complete") => void;
    isSubmitting: boolean;
    handlePdfExport: () => void;
    isViewOnlyMode: boolean;
  }) {
    // Create a custom component to replace toggle with dropdown in the subheader
    const CustomSubHeader: React.FC<{
      title?: string;
      id: string;
      testID?: string;
      item?: any; // Pass the whole item for debugging
    }> = ({ title, id, testID, item }) => {
      console.log("closeOutAnswers:", closeOutAnswers);
      const partnerId = (closeOutAnswers && closeOutAnswers[id]?.partner) || "";
      const isIpad = isIPadView();

      const handlePartnerChange = (partnerId: string) => {
        if (isViewOnlyMode) return;

        if (partnerId) {
          // If a partner is selected, save it
          dispatch(
            setCloseOutAnswer({
              id,
              partner: partnerId,
            })
          );
        } else {
          // If no partner is selected, remove the answer
          dispatch(removeCloseOutAnswer(id));
        }
      };

      // Get display title from item's ReviewFindings field or SiteEvaluationaForm as backup
      const displayTitle =
        item?.ReviewFindings ||
        item?.SiteEvaluationaForm ||
        title ||
        "Select Partner";

      return (
        <View>
          <View style={styles.customSubHeader} testID={testID}>
            <View style={styles.subHeaderContent}>
              <Text style={styles.subHeaderText}>{displayTitle}</Text>
            </View>
          </View>
          <View style={styles.selectPartnersSection}>
            {/* On iPad make the label wrapper match dropdown width and align right so '*Required' sits above the dropdown's right edge */}
            <View
              style={[
                styles.selectPartnersLabelWrapper,
                isIpad && {
                  width: '45%',
                  alignSelf: 'flex-start',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                },
              ]}
            >
              <Text style={styles.selectPartnersLabel}>Select Partners</Text>
              <Text style={styles.selectPartnersRequired}>*Required</Text>
            </View>
            <View style={[styles.dropdownContainer, isIpad && { width: '45%', alignSelf: 'flex-start' }]}>
              <CrossPlatformDropdown
                childList={COEUsers.map((u: any) => ({
                  id: u.UserEmail,
                  displayText: formatUserName(u.UserEmail),
                }))}
                label=""
                placeholder="Select Partners"
                selection="multi"
                selectedValue={
                  partnerId
                    ? partnerId.split(",").map((email) => ({
                      id: email.trim(),
                      displayText: formatUserName(email.trim()),
                    }))
                    : []
                }
                setSelectedValue={(value) => {
                  if (value && Array.isArray(value)) {
                    if (value.length > 0) {
                      // Join multiple partner emails with commas
                      const partnersString = value
                        .map((item) => item.id)
                        .join(",");
                      handlePartnerChange(partnersString);
                    } else {
                      // If all partners are removed
                      dispatch(removeCloseOutAnswer(id));
                    }
                  } else if (value && !Array.isArray(value)) {
                    // Handle single selection case (shouldn't happen with selection="multi")
                    handlePartnerChange(value.id.toString());
                  } else {
                    // Handle null case
                    dispatch(removeCloseOutAnswer(id));
                  }
                }}
                style={styles.dropdownStyle}
                isDisabled={isViewOnlyMode}
              />
            </View>
          </View>
        </View>
      );
    };

    // Override toggle handler for close out survey container - this is no longer used
    const handleToggleChange = (id: string, value: boolean, title: string) => {
      // No-op function to satisfy SiteEvalSurveyContainer props if needed
    };

    // Render closeOut data with proper structure
    const renderCloseOutData = () => {
      // Add console log to debug closeOutData structure
      console.log("CloseOutData in render function:", closeOutData);
      console.log(
        "First closeOutData item fields:",
        closeOutData && closeOutData.length > 0
          ? Object.keys(closeOutData[0])
          : "No data"
      );

      if (!closeOutData || closeOutData.length === 0) {
        return (
          <View style={styles.emptyDataContainer}>
            <Text style={styles.emptyDataText}>
              No close out data available
            </Text>
          </View>
        );
      }

      // Group data by headers and their subheaders
      const headerItems = closeOutData.filter((item) => item.isHeader);
      console.log("Found header items:", headerItems.length);

      // If no headers found, show all subheaders under a default header
      if (headerItems.length === 0) {
        const subHeaders = closeOutData.filter(
          (item) => item.toggleOnly || item.isParent
        );
        console.log("No headers found, using subheaders:", subHeaders.length);

        return (
          <View>
            <View style={styles.surveySection}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>
                  {closeOutData.find((item) => item.isHeader)?.ReviewFindings ||
                    "Close Out"}
                </Text>
              </View>

              {subHeaders.map((item) => (
                <CustomSubHeader
                  key={item.id}
                  id={item.id}
                  title={item.ReviewFindings || item.SiteEvaluationaForm}
                  item={item}
                  testID={`subheader-${item.id}`}
                />
              ))}
            </View>
          </View>
        );
      }

      // Otherwise, group and render by headers
      return (
        <View>
          {headerItems.map((header, headerIndex) => {
            // Get subheaders that belong to this header
            // If parentId matches header.id OR if no parentId and this is the first header
            const subHeaders = closeOutData.filter(
              (item) =>
                (item.toggleOnly || item.isParent) &&
                (item.parentId === header.id ||
                  (!item.parentId && headerIndex === 0))
            );

            console.log(
              `Found ${subHeaders.length} subheaders for header ${header.SiteEvaluationaForm}`
            );

            return (
              <View key={`header-${header.id}`} style={styles.surveySection}>
                {/* Header */}
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionHeaderText}>
                    {header.ReviewFindings ||
                      header.SiteEvaluationaForm ||
                      "Close Out"}
                  </Text>
                </View>

                {/* Subheaders with dropdowns */}
                {subHeaders.map((subHeader) => (
                  <CustomSubHeader
                    key={subHeader.id}
                    id={subHeader.id}
                    title={
                      subHeader.ReviewFindings || subHeader.SiteEvaluationaForm
                    }
                    item={subHeader}
                    testID={`subheader-${subHeader.id}`}
                  />
                ))}
              </View>
            );
          })}
        </View>
      );
    };

    return (
      <View style={styles.tabContainer}>
        <View style={styles.surveyContainer}>
          <ScrollView style={styles.closeoutScrollView}>
            {renderCloseOutData()}
          </ScrollView>
        </View>

        {/* Action Buttons Row removed from inside CloseOutlistTab - will be rendered globally below the survey card */}
      </View>
    );
  }

  const handleBackPress = () => {
    dispatch(resetSiteEvalRedirect());
    onBackPress();
  };

  // ...existing code...
  const handleRowClick = (rowData: any) => {
    console.log("Row clicked:", rowData);
    // Add your row click handling logic here
  };

  const loadingWorkData = useSelector(
    (state: any) => state.siteEvalRedirect.loadingWorkData
  );

  // Add state to track site evaluation details loading
  const [isLoadingSiteEvalDetails, setIsLoadingSiteEvalDetails] = useState(false);

  // Fetch site evaluation details when editing an existing evaluation
  React.useEffect(() => {
    const fetchSiteEvaluationDetails = async () => {
      // Only fetch if we have a selected evaluation ID (editing mode)
      if (!selectedSiteEvaluationId) {
        return;
      }

      // Avoid refetching if we already have answers loaded (unless user explicitly refreshed)
      const hasAnswers = walkAnswers && walkAnswers.length > 0;
      if (hasAnswers) {
        console.log('[SiteEvalRedirectScreen] Answers already loaded, skipping fetch');
        return;
      }

      setIsLoadingSiteEvalDetails(true);
      
      try {
        const { createApiClient, apiClient, getTimezoneOffsetString } = require("../../api/apiClient");
        
        // Check if this is a completed evaluation to skip org context params
        // For completed evaluations, we want the original question set (frozen at completion time)
        // For in-progress evaluations, we want questions filtered by current location org context
        const isCompletedEvaluation = status === "Completed";
        
        console.log(
          "[Site Eval Redirect] Loading questions - Status:",
          status,
          "isCompletedEvaluation:",
          isCompletedEvaluation
        );
        
        // Fetch questions data for existing evaluation
        const questionsApi = createApiClient();
        // new → org context from interceptor; in-progress → org context + recordId; completed → skip org context
        const locationParam = selectedLocation?.displayText ? `location=${encodeURIComponent(selectedLocation.displayText)}` : '';
        const recordIdParam = !isCompletedEvaluation && selectedSiteEvaluationId ? `recordId=${selectedSiteEvaluationId}` : '';
        const queryParams = [locationParam, recordIdParam].filter(Boolean).join('&');
        const questionsUrl = `/site-evaluation-enhanced/questions/all${queryParams ? `?${queryParams}` : ''}`;
        const questionsResponse = await safeSiteEvaluationApiCall<any>(
          (config?: any) => apiClient(questionsApi, questionsUrl, 'GET', undefined, config), 
          SITE_EVALUATION_API_NAMES.FETCH_QUESTIONS, 
          true, 
          { location: selectedLocation?.displayText }, 
          isCompletedEvaluation ? { skipOrgContext: true } : undefined
        );
        if (!questionsResponse) {
          setIsLoadingSiteEvalDetails(false);
          return;
        }
        const questionsArray = questionsResponse?.data ?? questionsResponse ?? [];
        
        // Map and dispatch questions data to Redux (for existing evaluations)
        const preWorkQuestions = questionsArray.filter((item: any) => 
          item.ID >= "1000" && item.ID < "2000"
        );
        const allWorkQuestions = questionsArray.filter((item: any) => 
          item.ID >= "2000"
        );
        const closeOutQuestions: any[] = [];
        
        // Import mapping functions
        const { mapApiWorkDataToAppFormat } = require('./SiteEvalRedirectScreen');
        const { mapApiPreWorkDataToAppFormat, mapApiCloseOutDataToAppFormat } = require('../../store/siteEvalRedirectSlice');
        
        // Dispatch mapped data to Redux
        dispatch(setWorkData(mapApiWorkDataToAppFormat(allWorkQuestions)));
        dispatch(setPreWorkData(mapApiPreWorkDataToAppFormat(preWorkQuestions)));
        dispatch(setCloseOutData(mapApiCloseOutDataToAppFormat(closeOutQuestions)));
        
        // Get or create action_plan_session_id and dispatch it to Redux so the overlay can read it
        const actionPlanSessionId = getOrCreateSiteEvalSessionId();
        dispatch(setActionPlanSessionId(actionPlanSessionId));
        console.log('[SiteEvalRedirectScreen] Using action_plan_session_id:', actionPlanSessionId);
        
        // Fetch site-evaluation-enhanced/{id}
        const api = createApiClient();
        const clientTimestamp = new Date().toISOString();
        const clientTimezone = getTimezoneOffsetString();
        const response = await safeSiteEvaluationApiCall<any>((config?: any) => apiClient(api, `/site-evaluation-enhanced/${selectedSiteEvaluationId}?clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`, "GET", undefined, config), SITE_EVALUATION_API_NAMES.FETCH_BY_ID, true, { id: selectedSiteEvaluationId });
        if (!response) {
          setIsLoadingSiteEvalDetails(false);
          return;
        }
        
        // Extract/normalize data from the response
        const responseData = response?.data ?? response;
        
        // Map walk data from the new API structure
        if (responseData?.walk && Array.isArray(responseData.walk)) {
          responseData.walk.forEach((walkItem: any) => {
            let mappedAnswer;
            if (walkItem.response === 1) mappedAnswer = "yes";
            else if (walkItem.response === 2) mappedAnswer = "no";
            else if (walkItem.response === 3) mappedAnswer = "na";
            else mappedAnswer = undefined;
            
            if (mappedAnswer) {
              dispatch(setWalkAnswer({ id: walkItem.question_id.toString(), answer: mappedAnswer }));
            }
          });
        }
        
        // Helper function to map section data
        const mapSectionData = (sectionData: any[], sectionName: string) => {
          if (sectionData && Array.isArray(sectionData)) {
            sectionData.forEach((item: any) => {
              let mappedAnswer;
              if (item.response === 1) mappedAnswer = "yes";
              else if (item.response === 2) mappedAnswer = "no";
              else if (item.response === 3) mappedAnswer = "na";
              else mappedAnswer = undefined;
              
              // Check if there's action plan data - if yes, default answer to "yes" if not set
              const hasActionPlan = !!(item.action_plan || item.supervisor || item.comment);
              
              // If there's an action plan but no explicit answer, default to "yes" so toggle is on
              if (hasActionPlan && !mappedAnswer) {
                mappedAnswer = "yes";
              }
              
              if (mappedAnswer) {
                dispatch(setWalkAnswer({ id: item.question_id.toString(), answer: mappedAnswer }));
              }
              
              // If there's action plan data, map overlay data
              if (hasActionPlan) {
                dispatch(
                  setWalkOverlayData({
                    id: item.question_id.toString(),
                    actionPlan: item.action_plan || item.comment || "",
                    supervisor: item.supervisor || "",
                    section_value: item.section_value || "",
                    parent_section_value: item.parent_section_value || "",
                    status: item.status || "",
                  })
                );
                
                // Store in Redux so SiteEvalActionPlanOverlay can access it
                const questionId = item.question_id.toString();
                const uniqueKey = `${selectedSiteEvaluationId}_${questionId}`;
                
                const actionPlanData = {
                  id: `${selectedSiteEvaluationId}_${questionId}`,
                  localId: Date.now() + parseInt(questionId),
                  itemId: uniqueKey,
                  relatedId: uniqueKey,
                  siteEvaluationId: selectedSiteEvaluationId,
                  questionId: questionId,
                  title: item.section_value || item.parent_section_value || 'Site Evaluation Action Plan',
                  actionPlan: item.action_plan || item.comment || "",
                  supervisor: item.supervisor || "",
                  assignee: item.supervisor || "",
                  source: 'Site Evaluation',
                  type: null,
                  priority: null,
                  startDate: null,
                  targetDate: null,
                  notes: "",
                  frontlineInvolvement: false,
                  employee: null,
                  employeeName: null,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  isReference: false,
                  status: item.status || 'Active'
                };
                
                // Dispatch to Redux - will update if exists or add new
                dispatch(updateSiteEvalActionPlan(actionPlanData));
                console.log('[SiteEvalRedirectScreen] Stored action plan in Redux for question:', questionId);
              }
            });
          }
        };
        
        // Map all section data
        mapSectionData(responseData?.vpe_mhe, "vpe_mhe");
        mapSectionData(responseData?.yard_gate, "yard_gate");
        mapSectionData(responseData?.layer_redzone, "layer_redzone");
        mapSectionData(responseData?.pick_area, "pick_area");
        mapSectionData(responseData?.deep_reserve, "deep_reserve");
        mapSectionData(responseData?.regulatory, "regulatory");
        mapSectionData(responseData?.lighthouse, "lighthouse");
        
        // Map action plans from API response
        if (responseData?.action_plans && Array.isArray(responseData.action_plans)) {
          responseData.action_plans.forEach((actionPlan: any) => {
            const questionId = actionPlan.capabilities?.[0]?.question_id?.toString();
            
            if (questionId && actionPlan.action_plan_description) {
              const uniqueKey = `${selectedSiteEvaluationId}_${questionId}`;
                
                // Map type string to dropdown option object
                let typeObj = null;
                if (actionPlan.type) {
                  const typeStr = actionPlan.type.toLowerCase();
                  if (typeStr === 'kpi') {
                    typeObj = { id: 'kpi', displayText: 'KPI' };
                  } else if (typeStr === 'kfa') {
                    typeObj = { id: 'kfa', displayText: 'KFA' };
                  } else if (typeStr === 'process') {
                    typeObj = { id: 'process', displayText: 'PROCESS' };
                  } else {
                    typeObj = { id: typeStr, displayText: actionPlan.type };
                  }
                }
                
                // Map priority string to dropdown option object
                let priorityObj = null;
                if (actionPlan.priority) {
                  const priorityStr = actionPlan.priority.toLowerCase();
                  if (priorityStr === 'high') {
                    priorityObj = { id: 'high', displayText: 'High' };
                  } else if (priorityStr === 'medium') {
                    priorityObj = { id: 'medium', displayText: 'Medium' };
                  } else if (priorityStr === 'low') {
                    priorityObj = { id: 'low', displayText: 'Low' };
                  } else {
                    priorityObj = { id: priorityStr, displayText: actionPlan.priority };
                  }
                }
                
                const actionPlanData = {
                  id: actionPlan.id ? `action_plan_${actionPlan.id}` : `${selectedSiteEvaluationId}_${questionId}`,
                localId: Date.now() + parseInt(questionId),
                itemId: uniqueKey,
                relatedId: uniqueKey,
                siteEvaluationId: selectedSiteEvaluationId,
                questionId: questionId,
                title: actionPlan.title || 'Site Evaluation Action Plan',
                actionPlan: actionPlan.action_plan_description || "",
                supervisor: actionPlan.assignee_email || "",
                assignee: actionPlan.assignee_email || "",
                selectedAssignee: actionPlan.assignee_email ? {
                  id: actionPlan.assignee_email,
                  email: actionPlan.assignee_email,
                  gpid: actionPlan.assignee_gpid || "",
                  role: actionPlan.assignee_role || "",
                  roleId: actionPlan.assignee_role_id || 0,
                  displayText: formatUserName(actionPlan.assignee_email) || actionPlan.assignee_email
                } : null,
                source: 'Site Evaluation',
                type: typeObj,
                priority: priorityObj,
                startDate: actionPlan.start_date ? new Date(actionPlan.start_date) : null,
                targetDate: actionPlan.target_date ? new Date(actionPlan.target_date) : null,
                notes: actionPlan.notes || "",
                frontlineInvolvement: actionPlan.frontline_employee_involvement || false,
                employee: actionPlan.involved_employee_gpids || null,
                employeeName: actionPlan.involved_employee_names || null,
                selectedEmployee: actionPlan.involved_employee_gpids ? {
                  id: actionPlan.involved_employee_gpids,
                  gpid: actionPlan.involved_employee_gpids,
                  name: actionPlan.involved_employee_names || "",
                  displayText: actionPlan.involved_employee_names || ""
                } : null,
                createdAt: actionPlan.created_at || new Date().toISOString(),
                updatedAt: actionPlan.updated_at || new Date().toISOString(),
                isReference: false,
                status: actionPlan.status || 'Active',
                actionPlanId: actionPlan.id,
                rawFormData: {
                  title: actionPlan.title || 'Site Evaluation Action Plan',
                  selectedType: typeObj,
                  selectedPriority: priorityObj,
                  selectedAssignee: actionPlan.assignee_email ? {
                    id: actionPlan.assignee_email,
                    email: actionPlan.assignee_email,
                    gpid: actionPlan.assignee_gpid || "",
                    role: actionPlan.assignee_role || "",
                    roleId: actionPlan.assignee_role_id || 0,
                    displayText: formatUserName(actionPlan.assignee_email) || actionPlan.assignee_email
                  } : null,
                  startDate: actionPlan.start_date ? new Date(actionPlan.start_date) : null,
                  targetDate: actionPlan.target_date ? new Date(actionPlan.target_date) : null,
                  actionPlan: actionPlan.action_plan_description || "",
                  notes: actionPlan.notes || "",
                  frontlineInvolvement: actionPlan.frontline_employee_involvement || false,
                  selectedEmployee: actionPlan.involved_employee_gpids ? {
                    id: actionPlan.involved_employee_gpids,
                    gpid: actionPlan.involved_employee_gpids,
                    name: actionPlan.involved_employee_names || "",
                    displayText: actionPlan.involved_employee_names || ""
                  } : null,
                }
              };
              
              // Dispatch to Redux - will update if exists or add new
              dispatch(updateSiteEvalActionPlan(actionPlanData));
            }
          });
          
          // Extract involved employees for close out review
          const involvedEmployees = responseData.action_plans
            .filter((actionPlan: any) => actionPlan.involved_employee_names)
            .map((actionPlan: any) => actionPlan.involved_employee_names)
            .join(",");
            
          if (involvedEmployees) {
            dispatch(setCloseOutAnswer({ id: 2, partner: involvedEmployees }));
          }
        }
        
        // Map prework data from the API response
        if (responseData?.prework && Array.isArray(responseData.prework)) {
          const preWorkQuestionsData = questionsResponse.filter((q: any) => 
            q.ID >= "1000" && q.ID < "2000" && q.Section_Type === "DATA"
          );
          
          const mappedPreWorkData = responseData.prework
            .map((preWorkItem: any) => {
              const questionData = preWorkQuestionsData.find((q: any) => 
                q.ID === preWorkItem.question_id.toString()
              );
              
              if (!questionData) {
                return null;
              }
              
              return {
                id: preWorkItem.question_id.toString(),
                priorVisit: preWorkItem.prior_visit !== undefined && preWorkItem.prior_visit !== null && !isNaN(Number(preWorkItem.prior_visit))
                  ? Number(Number(preWorkItem.prior_visit).toFixed(2))
                  : 0,
                lcp: preWorkItem.lcp !== undefined && preWorkItem.lcp !== null && !isNaN(Number(preWorkItem.lcp))
                  ? Number(Number(preWorkItem.lcp).toFixed(2))
                  : 0,
                ytd: preWorkItem.ytd !== undefined && preWorkItem.ytd !== null && !isNaN(Number(preWorkItem.ytd))
                  ? Number(Number(preWorkItem.ytd).toFixed(2))
                  : 0,
                review: questionData?.Section_Value || `Question ${preWorkItem.question_id}`,
              };
            })
            .filter((item: any) => item !== null);
          
          dispatch(setPreWorkAnswers(mappedPreWorkData));
        }
        
        console.log('[SiteEvalRedirectScreen] Successfully loaded site evaluation details');
      } catch (error: any) {
        console.error('[SiteEvalRedirectScreen] Failed to fetch site evaluation details:', error);
        if (showStatusMessage) {
          showStatusMessage("error", "Failed to load site evaluation details. Please try again.");
        }
      } finally {
        setIsLoadingSiteEvalDetails(false);
      }
    };

    fetchSiteEvaluationDetails();
  }, [selectedSiteEvaluationId, selectedLocation?.displayText, dispatch]);

  React.useEffect(() => {
    const hasAnyQuestionData =
      (Array.isArray(preWorkData) && preWorkData.length > 0) ||
      (Array.isArray(workData) && workData.length > 0);

    // Only fetch questions for new evaluations (not when editing existing ones)
    // The editing useEffect above handles fetching for existing evaluations
    if (!hasAnyQuestionData && !selectedSiteEvaluationId) {
      dispatch(fetchAllQuestionsData(selectedLocation?.displayText));
    }
  }, [dispatch, selectedLocation?.displayText, preWorkData?.length, workData?.length, selectedSiteEvaluationId]);

  // Use ScrollView for iOS, regular View for web and other platforms
  const ContainerComponent = Platform.OS === "ios" ? ScrollView : View;
  const containerProps =
    Platform.OS === "ios"
      ? { style: [styles.container, isDark && { backgroundColor: colors.background.page }], showsVerticalScrollIndicator: false }
      : { style: [styles.container, isDark && { backgroundColor: colors.background.page }] };

  // Breadcrumb items
  const breadcrumbItems: DLOBreadcrumbItem[] = [
    {
      id: 'site-evaluation',
      label: 'Site Evaluation',
      onPress: onBackPress,
    },
    {
      id: 'new-evaluation',
      label: 'New Evaluation',
      isCurrentPage: true,
    },
  ];

  // Show breadcrumb on web and iPad, back button only on mobile (not iPad)
  const showBreadcrumb = Platform.OS === 'web' || isIPadView();
  const showMobileBack = isMobileView() && !isIPadView();

  return (
    <>
    <ContainerComponent {...containerProps}>
      {/* Breadcrumb Navigation - Web/iPad only, Back button for mobile */}
      {showMobileBack && (
        <View style={styles.mobileHeaderContainer}>
          <TouchableOpacity onPress={onBackPress} style={styles.mobileBackButton}>
            <MaterialIcons name="arrow-back" size={24} color={isDark ? colors.brand.primary : "#005CBC"} />
            <Text style={[styles.mobileBackButtonText, isDark && { color: colors.brand.primary }]}> Back to Site Evaluation</Text>
          </TouchableOpacity>
        </View>
      )}
      {showBreadcrumb && (
        <View style={styles.breadcrumbContainer}>
          <DLOBreadcrumb
            items={breadcrumbItems}
            showHomeIcon={true}
            onHomePress={onHomePress}
            style={{ flexDirection: 'row', alignItems: 'center' }}
          />
        </View>
      )}
      {/* Stepper style navigation (replaces TabbedNavigation) */}
      <StepperNavigation
        active={activeTab}
        onChange={handleTabChange}
        style={{ marginBottom: 28 }}
        disabled={loadingWorkData || isLoadingSiteEvalDetails}
      />
      <View style={[styles.tabContentContainer, isDark && { backgroundColor: colors.background.surface }]}>
        {(loadingWorkData || isLoadingSiteEvalDetails) ? (
          <LHLoader testID="loading" />
        ) : (
          <>
            {activeTab === "pre-work" && <PreWorkTab handleRowClick={handleRowClick} isViewOnlyMode={isViewOnlyMode} selectedEvalId={selectedSiteEvaluationId} />}
            {activeTab === "walk" && (
              <WalkTab
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                setActiveTab={setActiveTab}
                isViewOnlyMode={isViewOnlyMode}
                selectedEvalId={selectedSiteEvaluationId}
              />
            )}
            {activeTab === "vpe-mhe" && (
              <VpeMheTab
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                setActiveTab={setActiveTab}
                isViewOnlyMode={isViewOnlyMode}
                selectedEvalId={selectedSiteEvaluationId}
              />
            )}
            {activeTab === "yard-gate" && (
              <YardGateTab
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                setActiveTab={setActiveTab}
                isViewOnlyMode={isViewOnlyMode}
                selectedEvalId={selectedSiteEvaluationId}
              />
            )}
            {activeTab === "layer-redzone" && (
              <LayerRedzoneTab
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                setActiveTab={setActiveTab}
                isViewOnlyMode={isViewOnlyMode}
                selectedEvalId={selectedSiteEvaluationId}
              />
            )}
            {activeTab === "pick-area" && (
              <PickAreaTab
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                setActiveTab={setActiveTab}
                isViewOnlyMode={isViewOnlyMode}
                selectedEvalId={selectedSiteEvaluationId}
              />
            )}
            {activeTab === "deep-reserve" && (
              <DeepReserveTab
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                setActiveTab={setActiveTab}
                isViewOnlyMode={isViewOnlyMode}
                selectedEvalId={selectedSiteEvaluationId}
              />
            )}
            {activeTab === "regulatory" && (
              <RegulatoryTab
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                setActiveTab={setActiveTab}
                isViewOnlyMode={isViewOnlyMode}
                selectedEvalId={selectedSiteEvaluationId}
              />
            )}
            {activeTab === "lighthouse" && (
              <LighthouseTab
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                setActiveTab={setActiveTab}
                isViewOnlyMode={isViewOnlyMode}
                selectedEvalId={selectedSiteEvaluationId}
              />
            )}
          </>
        )}
      </View>

      {/* Global action row rendered outside / below the white survey card */}
      <View style={[styles.buttonContainer, Platform.OS === 'ios' && isMobileView() ? styles.buttonContainerVertical : null]}>
        {/* If we're on the lighthouse tab (final tab), render the full redirect action component (PDF/Save/Complete) */}
        {activeTab === 'lighthouse' ? (
          <SiteEvalRedirectAction
            isSubmitting={isSubmitting}
            isPdfExporting={isPdfExporting}
            status={status}
            selectedRecordId={selectedSiteEvaluationId}
            userEmail={userEmail}
            onSave={() => handleSubmit('save')}
            onComplete={() => handleSubmit('complete')}
            onExportPdf={handlePdfExport}
            onBack={() => {
              // Navigate to previous step in the flow
              setActiveTab('regulatory');
            }}
            isDark={isDark}
            colors={colors}
          />
        ) : (
          // Default Back / Save / Next row for non-lighthouse tabs
          <>
            {isIPadView() ? (
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                {/* Back button on the left */}
                <View style={{ flex: 1, alignItems: 'flex-start' }}>
                  <TouchableOpacity
                    onPress={() => {
                      if (activeTab === 'pre-work') {
                        setConfirmNavigationTarget('home');
                        setShowConfirmDialog(true);
                        return;
                      }
                      const prevMap: Record<string, string> = {
                        'pre-work': 'pre-work',
                        'vpe-mhe': 'pre-work',
                        'yard-gate': 'vpe-mhe',
                        'layer-redzone': 'yard-gate',
                        'pick-area': 'layer-redzone',
                        'deep-reserve': 'pick-area',
                        'regulatory': 'deep-reserve',
                        'lighthouse': 'regulatory',
                      };
                      const target = prevMap[activeTab || 'pre-work'] || 'pre-work';
                      setActiveTab(target);
                    }}
                    disabled={false}
                    style={[
                      styles.backButton,
                      isDark && {
                        backgroundColor: 'transparent',
                        borderColor: '#005CBC',
                      },
                      {
                        // Reduced width for iPad so Back doesn't appear too large
                        minWidth: 90,
                        maxWidth: 160,
                        width: 140,
                      },
                    ]}
                  >
                    <Text style={[styles.backButtonText, isDark && { color: '#005CBC' }]}>Back</Text>
                  </TouchableOpacity>
                </View>
                {/* Save for Later and Next buttons with gap */}
                <View style={{ flex: 1.5, flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 16, paddingRight: 50 }}>
                  <LhButton
                    text="Save for Later"
                    iconLeading="save"
                    iconSize={20}
                    variant="primary"
                    size="small"
                    onPress={() => handleSubmit('save')}
                    isLoading={isSubmitting}
                    disabled={isSubmitting || isViewOnlyMode}
                    style={{
                      paddingVertical: 8,
                      paddingHorizontal: 18,
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: isDark ? 'transparent' : '#FFFFFF',
                      borderWidth: 1,
                      borderColor: isDark ? '#005CBC' : '#034895',
                      borderRadius: 24,
                      height: 40,
                      minWidth: 160, // Decreased width for iPad
                      maxWidth: 130,
                    }}
                    textStyle={{
                      color: isDark ? '#005CBC' : '#034895',
                      fontFamily: Platform.select({ web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif', ios: 'Inter', android: 'Inter' }),
                      fontSize: 16,
                      fontStyle: 'normal',
                      fontWeight: '700',
                      lineHeight: 22,
                    }}
                  />
                  <LhButton
                    text="Next"
                    variant="primary"
                    size="small"
                    onPress={() => {
                      const nextMap: Record<string, string> = {
                        'pre-work': 'vpe-mhe',
                        'vpe-mhe': 'yard-gate',
                        'yard-gate': 'layer-redzone',
                        'layer-redzone': 'pick-area',
                        'pick-area': 'deep-reserve',
                        'deep-reserve': 'regulatory',
                        'regulatory': 'lighthouse',
                        'lighthouse': 'lighthouse',
                      };
                      const nextTab = nextMap[activeTab] || 'lighthouse';
                      handleTabChange(nextTab);
                    }}
                    style={{
                      ...styles.completeButton,
                      backgroundColor: '#034895',
                      borderColor: '#034895',
                      borderRadius: 24,
                      height: 40,
                      minWidth: 160, // Decreased width for iPad
                      maxWidth: 130,
                    }}
                    textStyle={{
                      overflow: 'hidden',
                      color: '#FFFFFF',
                      fontFamily: Platform.select({ web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif', ios: 'Inter', android: 'Inter' }),
                      fontSize: 16,
                      fontStyle: 'normal',
                      fontWeight: '700',
                      lineHeight: 22,
                    }}
                    disabled={!isViewOnlyMode && !validateSectionCompletion(activeTab)}
                  />
                </View>
              </View>
            ) : (
              <>
                <View style={[styles.leftButtons, Platform.OS === 'ios' && isMobileView() ? styles.leftButtonsVertical : null]}>
                  <TouchableOpacity
                    onPress={() => {
                      if (activeTab === 'pre-work') {
                        setConfirmNavigationTarget('home');
                        setShowConfirmDialog(true);
                        return;
                      }
                      const prevMap: Record<string, string> = {
                        'pre-work': 'pre-work',
                        'vpe-mhe': 'pre-work',
                        'yard-gate': 'vpe-mhe',
                        'layer-redzone': 'yard-gate',
                        'pick-area': 'layer-redzone',
                        'deep-reserve': 'pick-area',
                        'regulatory': 'deep-reserve',
                        'lighthouse': 'regulatory',
                      };
                      const target = prevMap[activeTab || 'pre-work'] || 'pre-work';
                      setActiveTab(target);
                    }}
                    disabled={false}
                    style={[
                      styles.backButton,
                      isDark && {
                        backgroundColor: 'transparent',
                        borderColor: '#005CBC',
                      },
                    ]}
                  >
                    <Text style={[styles.backButtonText, isDark && { color: '#005CBC' }]}>Back</Text>
                  </TouchableOpacity>
                </View>

                <View style={[styles.rightButtons, Platform.OS === 'ios' && isMobileView() ? styles.rightButtonsVertical : null]}>
                  <LhButton
                    text="Save for Later"
                    iconLeading="save"
                    iconSize={20}
                    variant="primary"
                    size="small"
                    onPress={() => handleSubmit('save')}
                    isLoading={isSubmitting}
                    disabled={isSubmitting || isViewOnlyMode}
                    style={{
                      paddingVertical: 8,
                      paddingHorizontal: 24,
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: isDark ? 'transparent' : '#FFFFFF',
                      borderWidth: 1,
                      borderColor: isDark ? '#005CBC' : '#034895',
                      borderRadius: 24,
                      height: 40,
                      minWidth: Platform.OS === 'web' ? 160 : undefined,
                      marginRight: 10,
                    }}
                    textStyle={{
                      color: isDark ? '#005CBC' : '#034895',
                      fontFamily: Platform.select({ web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif', ios: 'Inter', android: 'Inter' }),
                      fontSize: 16,
                      fontStyle: 'normal',
                      fontWeight: '700',
                      lineHeight: 22,
                    }}
                  />

                  <LhButton
                    text="Next"
                    variant="primary"
                    size="small"
                    onPress={() => {
                      const nextMap: Record<string, string> = {
                        'pre-work': 'vpe-mhe',
                        'vpe-mhe': 'yard-gate',
                        'yard-gate': 'layer-redzone',
                        'layer-redzone': 'pick-area',
                        'pick-area': 'deep-reserve',
                        'deep-reserve': 'regulatory',
                        'regulatory': 'lighthouse',
                        'lighthouse': 'lighthouse',
                      };
                      const nextTab = nextMap[activeTab] || 'lighthouse';
                      handleTabChange(nextTab);
                    }}
                    style={{
                      ...styles.completeButton,
                      backgroundColor: '#034895',
                      borderColor: '#034895',
                      borderRadius: 24,
                      height: 40,
                      minWidth: Platform.OS === 'web' ? 140 : undefined,
                    }}
                    textStyle={{
                      overflow: 'hidden',
                      color: '#FFFFFF',
                      fontFamily: Platform.select({ web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif', ios: 'Inter', android: 'Inter' }),
                      fontSize: 16,
                      fontStyle: 'normal',
                      fontWeight: '700',
                      lineHeight: 22,
                    }}
                    disabled={!isViewOnlyMode && !validateSectionCompletion(activeTab)}
                  />
                </View>
              </>
            )}
          </>
        )}
      </View>


      {/* Confirmation dialog shown when leaving Pre-Work tab */}
      <LHConfirmationDialog
        visible={showConfirmDialog}
        message={"You have unsaved data, are you sure you want to go back?"}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
      />
    </ContainerComponent>

    {/* Mount centralized error modal outside of container to avoid clipping issues */}
    <SiteEvaluationErrorModal />
    </>
  );
};

const styles = StyleSheet.create({
  container: {

    paddingTop: Platform.select({ web: 20, default: 20 }),
    paddingBottom: Platform.select({ web: 20, default: 100 }),
    paddingHorizontal: Platform.select({ web: 16, default: 10 }),
    backgroundColor: "#F5F9FF",
    minHeight: "100%",
    ...(Platform.OS === "ios" && { flex: 1 }), // Added for better iOS layout only
  },
  closeoutItemContainer: {
    marginBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EEEEEE",
    paddingBottom: 16,
  },
  closeoutItemHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  closeoutItemText: {
    fontSize: 16,
    fontWeight: "500",
    color: "#333333",
    flex: 1,
    paddingRight: 10,
  },
  closeoutItemDropdown: {
    marginBottom: 10,
  },
  closeoutScrollView: {
    flex: 1,
  },
  // New styles for the custom subheader with dropdown
  customSubHeader: {
    backgroundColor: "#F5F9FF",
    padding: 12,
    borderRadius: 4,
    marginBottom: 10,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#EEEEEE",
    ...(Platform.OS === "web" && {
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
    }),
  },
  subHeaderContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    // paddingHorizontal: 10,
  },
  subHeaderText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#2D2D2D",
    flex: 1,
    marginRight: 10,
    fontFamily: FontFamily.bold,
    lineHeight: 18,
  },
  dropdownContainer: {
    width: Platform.OS === "web" ? "35%" : "100%",
    maxWidth: "100%",
    paddingVertical: 6,
    marginLeft: 0,
  },
  dropdownStyle: {
    width: "100%",
    marginTop: 0,
    marginBottom: 0,
  },
  // New styles for the survey sections
  // surveySections: {
  //   paddingVertical: 8,
  // },
  surveySection: {
    marginBottom: 16,
  },
  sectionHeader: {
    backgroundColor: Platform.OS === 'web' ? '#0b66bf' : '#034895',
    padding: 12,
    borderRadius: 4,
    // marginVertical: 8,
  },
  sectionHeaderText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "700",
    fontFamily: FontFamily.bold,
    lineHeight: 20,
    // alignContent: "center",
    textAlign: "center",
  },
  sectionItem: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E9ECEF",
  },
  itemText: {
    fontSize: 14,
    color: "#343A40",
  },
  emptyDataContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyDataText: {
    fontSize: 16,
    color: "#6C757D",
  },
  tabContentContainer: {
    ...(Platform.OS !== "web" && { flex: 1 }), // Only apply flex for mobile platforms
    minHeight: Platform.select({
      ios: 400,
      android: 300,
      web: 200,
      default: 300,
    }),
    backgroundColor: "transparent", // Change to transparent to remove white background
    paddingBottom: Platform.select({
      ios: 20,
      web: 0,
      android: 16,
      default: 16,
    }),
  },
  tabContainer: {
    ...(Platform.OS !== "web" && { flex: 1 }), // Only apply flex for mobile platforms
    paddingHorizontal: Platform.select({
      ios: 16,
      android: 16,
      web: 0,
      default: 16,
    }),
    paddingVertical: Platform.select({
      ios: 20,
      android: 16,
      web: 0,
      default: 16,
    }),
    backgroundColor: "transparent",
    minHeight: Platform.select({
      ios: 300,
      android: 250,
      web: 250,
      default: 250,
    }), // Smaller for web
    ...(Platform.OS === "ios" && {
      borderRadius: 8, // Rounded corners for iOS only
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 3,
    }),
    ...(Platform.OS === "android" && { elevation: 2 }),
  },
  overlayLabel: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#343A40",
    marginBottom: 6,
    alignSelf: "flex-start",
    paddingLeft: 20,
  },
  overlayContent: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 5,
    alignItems: "center",
  },
  dropdown: {
    marginTop: 12,
    width: "100%",
    paddingHorizontal: 20,
  },
  actionPlanInput: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    marginHorizontal: 20,
    minHeight: 100,
    fontSize: 16,
    backgroundColor: "#fff",
  },
  breadcrumbContainer: {
    marginBottom: 16,
    marginLeft: 18, // Adjust for container padding
  },
  mobileHeaderContainer: {
    marginBottom: 16,
    marginLeft: 0,
  },
  mobileBackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 0,
    gap: 8,
  },
  mobileBackButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#005CBC',
    fontFamily: FontFamily.bold,
    lineHeight: 22,
  },
  laterButton: {
    // Reduced width: don't grow to fill available space; use explicit width on web
    flex: 0,
    paddingVertical: 8,
    paddingHorizontal: 24,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#034895',
    borderRadius: 24,
    // increased width for web per request
    width: Platform.OS === 'web' ? 200 : undefined,
    minWidth: Platform.OS === 'web' ? 140 : undefined,
  },
  completeButton: {
    width: Platform.OS === "web" ? 120 : "100%",
    minWidth: 140,
    backgroundColor: '#034895',
    paddingVertical: 8,
    paddingHorizontal: 24,
    height: 40,
    borderRadius: 24,
  },
  overlayCentered: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    backgroundColor: "rgba(0,0,0,0.3)",
  },
  overlayContentFixed: {
    width: 350,
    maxWidth: "90%",
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 5,
    alignItems: "center",
  },
  overlayDropdownFixed: {
    width: "100%",
    marginTop: 16,
    marginBottom: 16,
    paddingHorizontal: 0,
    alignSelf: "center",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    // marginTop: 20,
    paddingHorizontal: Platform.OS === "web" ? 16 : 10,
    paddingBottom: Platform.OS === "ios" ? 30 : 0,
  },
  leftButtons: {
    flex: 1,
    alignItems: "flex-start",
  },
  rightButtons: {
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-end",
  },
  surveyContainer: {
    flex: 1,
    marginBottom: 20,
    minHeight: Platform.select({
      ios: 250, // Ensure minimum height for iOS visibility
      android: 200,
      web: 200, // Keep web default smaller
      default: 200,
    }),
    backgroundColor: "#fff",
    ...(Platform.OS === "web" && {
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
    }),
  },
  overlayButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
    marginTop: 20,
    paddingHorizontal: 20,
  },
  overlayButton: {
    flex: 1,
  },
  dropdownView: {
    marginTop: -10,
  },
  // iPhone-specific vertical button layout styles
  buttonContainerVertical: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12,
  },
  leftButtonsVertical: {
    flex: 0,
    alignItems: "stretch",
    marginRight: 0,
  },
  rightButtonsVertical: {
    flexDirection: "column",
    alignItems: "stretch",
    marginLeft: 0,
  },
  laterButtonVertical: {
    width: "100%",
  },
  completeButtonVertical: {
    width: "100%",
  },
  // Stepper styles
  stepperContainer: {
    width: '100%',
    alignItems: 'flex-start',
    marginVertical: 12,
    marginLeft: Platform.OS === 'ios' ? 8 : 24,
    marginRight: Platform.OS === 'ios' ? 8 : 24,
    paddingHorizontal: Platform.OS === 'ios' ? 8 : 0,
  },
  stepperInner: {
    width: '100%',
    maxWidth: 900,
    paddingVertical: 12, // ensure there's space above/below circles for the line to center
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
    position: 'relative',
  },
  stepLineWrapper: {
    position: 'absolute',
    left: 20,
    right: 20,
    height: 28, // container height for the line
    top: 12, // align with circle center given paddingVertical
    zIndex: 0,
    justifyContent: 'center',
  },
  stepLineRow: {
    flexDirection: 'row',
    height: 2,
    width: '100%',
    borderRadius: 1,
    overflow: 'hidden',
  },
  stepLineBackgroundSegment: {
    backgroundColor: '#E0E0E0',
  },
  stepLineCompleted: {
    backgroundColor: '#005CBC',
  },
  circlesRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    // paddingHorizontal: 20, // match left/right offsets so line aligns with centers
    zIndex: 2, // bring circles above line

  },
  stepItemTouchable: {
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
    width: 40, // fixed width so center positions are predictable
  },
  labelsRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    marginTop: 5,
    zIndex: 2,
  },
  stepLabelWrapper: {
    width: '33%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepCircle: {
    width: 10,
    height: 10,
    borderRadius: 5,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 0,
    backgroundColor: '#E6E9EB',
  },
  stepCircleActive: {
    // Current tab: grey circle with blue border
    borderWidth: 4,
    borderColor: '#005CBC',
    backgroundColor: '#E6E9EB',
  },
  stepCircleCompleted: {
    // Completed tab: blue filled circle with checkmark
    borderWidth: 0,
    backgroundColor: '#005CBC',
  },
  stepCircleInactive: {
    // Unvisited tab: grey circle matching line
    borderWidth: 0,
    backgroundColor: '#E6E9EB',
  },
  stepInnerCircle: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#005CBC',
  },
  stepLabel: {
    fontSize: 14,
    textAlign: 'center',
    width: '100%',
  },
  stepLabelActive: {
    color: '#005CBC',
    fontWeight: '700',
  },
  stepLabelInactive: {
    color: '#6C757D',
    fontWeight: '500',
  },
  tableCard: {
    backgroundColor: '#ffffff',
    borderRadius: Platform.select({ ios: 8, android: 8, web: 8, default: 8 }),
    padding: Platform.select({ web: 24, ios: 24, default: 0 }),
    // On iOS/iPadOS, add subtle shadows
    ...(Platform.OS === 'ios' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.08,
      shadowRadius: 4,
      // Add specific margin for iOS
      marginHorizontal: isMobileView() ? 16 : 0,
      marginVertical: 8,
    }),
    // On Android, use elevation
    ...(Platform.OS === 'android' && {
      elevation: 2,
      marginVertical: 8
    }),
    // On web, just use margin
    ...(Platform.OS === 'web' && {
      marginVertical: 8
    }),
    // Common styles
    borderWidth: 0,
    borderColor: 'transparent',
    flex: 1,
  },
  backButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#034895',
    borderRadius: 24,
    height: 40,
    minWidth: Platform.OS === 'web' ? 140 : undefined,
    marginRight: 10,
  },
  backButtonText: {
    color: '#034895',
    fontFamily: Platform.select({ web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif', ios: 'Inter', android: 'Inter' }),
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 22,
  },
  selectPartnersSection: {
    marginTop: 12,
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 8,
    backgroundColor: '#FFFFFF',
    // borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
    marginBottom: 10,
    // ...(Platform.OS === "web" && {
    //   boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
    // }),
  },
  selectPartnersLabelWrapper: {
    position: 'relative',
    paddingRight: 12,
    marginBottom: 10,
  },
  selectPartnersLabel: {
    fontSize: 12,
    fontWeight: '400',
    color: '#3A3A3A',
    fontFamily: FontFamily.regular,
  },
  selectPartnersRequired: {
    position: 'absolute',
    right: 0,
    top: 2,
    color: '#616161',
    textAlign: 'right',
    fontSize: 10,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 14,
    fontFamily: FontFamily.medium,
    paddingRight: isMobileView() ? 0 : 900,
  },
  addActionPlanLink: {
    marginTop: 12,
  },
  addActionPlanText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0066CC',
    textDecorationLine: 'none',
  },
  // Figma-based button styles for overlay
  cancelButton: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 24,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 80,
    maxWidth: 100,
  },
  cancelButtonText: {
    color: '#005CBC',
    fontFamily: Platform.select({
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
      ios: 'Inter',
      android: 'Inter'
    }),
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 20,
  },
  saveButton: {
    backgroundColor: '#005CBC',
    borderWidth: 0,
    paddingVertical: 4,
    paddingHorizontal: 16,
    borderRadius: 24,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 80,
    maxWidth: 100,
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
      ios: 'Inter',
      android: 'Inter'
    }),
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 20,
  },
  mobilePreWorkContainer: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  preWorkCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    overflow: 'hidden',
  },
  preWorkCardHeader: {
    backgroundColor: '#034895',
    paddingHorizontal: 16,
    paddingVertical: 15,
  },
  preWorkCardHeaderText: {
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 18,
  },
  preWorkCardContent: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 0,
  },
  preWorkDataSection: {
    marginBottom: 12,
  },
  preWorkDataLabel: {
    color: '#2D2D2D',
    fontFamily: FontFamily.regular,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    marginBottom: 8,
  },
  preWorkDataLabelBold: {
    fontFamily: FontFamily.bold,
    fontWeight: '700',
  },
  preWorkActionsSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: '#E8EDF2',
    marginTop: 4,
    paddingVertical: 10,
  },
  // Target button styles (matching Figma design)
  targetButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: '#FBFCFE',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 80,
    height: 32,
  },
  targetButtonLeft: {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  targetButtonRight: {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
  targetButtonActive: {
    backgroundColor: '#005CBC',
  },
  targetButtonText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    color: '#005CBC',
    textAlign: 'center',
  },
  targetButtonActiveText: {
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  preWorkButtonGroup: {
    flexDirection: 'row',
    flexShrink: 0,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    backgroundColor: '#D7E5F8',
    overflow: 'hidden',
  },
  preWorkButton: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: '#FBFCFE',
    justifyContent: 'center',
    alignItems: 'center',
  },
  preWorkButtonLeft: {
    borderRightWidth: 1,
    borderRightColor: '#D7E5F8',
  },
  preWorkButtonRight: {
    // No additional styles needed
  },
  preWorkButtonActive: {
    backgroundColor: '#005CBC',
  },
  preWorkButtonText: {
    color: '#005CBC',
    fontFamily: FontFamily.regular,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center',
  },
  preWorkButtonActiveText: {
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
    fontWeight: '700',
  },
  dividerSmall: {
    width: 1,
    backgroundColor: '#D7E5F8',
    height: 32,
    alignSelf: 'center',
  },
  preWorkActionPlanLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  preWorkActionPlanText: {
    color: '#005CBC',
    fontFamily: FontFamily.bold,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 18,
  },
  preWorkInfoIcon: {
    marginLeft: 4,
  },
  actionPlanCell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  actionPlanCellCompleted: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  actionPlanText: {
    color: '#034895',
    fontFamily: FontFamily.medium,
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
  },
  actionPlanTextCompleted: {
    color: '#005CBC',
    fontFamily: FontFamily.bold,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 18,
  },
});

export default SiteEvalRedirectScreen;
